/**
 * Provider Calendar page – schedule and manage appointments (demo with dummy data).
 * Provider-only; month/week/day views with create/edit/delete event modal.
 * Events are draggable in week and day views and snap to 15-minute intervals.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatHumanName } from '@medplum/core';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Appointment, AppointmentParticipant, Group as FhirGroup, Patient, Practitioner } from '@medplum/fhirtypes';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import {
  EPISODE_OF_CARE_CONTEXT_EXT,
  RECURRING_PATTERN_EXT,
  THERAPY_SERVICE_TYPE_SYSTEM,
  VIDEO_MEETING_URL_EXT,
} from '@unify/fhir-model/constants';
import { getGroupFacilitators, getGroupTemplateRef } from '@unify/fhir-model/resources/group';
import { getGroupTemplateSessionHealthcareService } from '@unify/fhir-model/resources/group-template';
import {
  getActiveGroupMemberPatientIds,
  requestAppointmentSeriesSyncParticipants,
  searchTherapyGroups,
} from '@/lib/groupTherapy';
import { applyClinicianCancelToAppointment } from '@unify/fhir-model/resources/appointment';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';
import { AppointmentEditorModal } from '@/features/calendar/AppointmentEditorModal';
import { useLocationResources } from '../../hooks/useLocationResources';
import { useRoomAvailabilityForDate } from '../../hooks/useRoomAvailabilityForDate';
import { buildMockAppointmentPageStateFromCalendarEvent } from '../appointments/mockAppointmentPageData';
import { isHumanParticipantDisplay } from '../appointments/utils/fhirHelpers';
import { buildAppointmentDisplayTitle, resolveSessionAppointmentTitle } from '../appointments/utils/buildAppointmentDisplayTitle';
import {
  buildCalendarStatusSelectData,
  getCalendarStatusTransitionDisabledReason,
} from '../appointments/utils/appointmentWorkflowStatus';
import {
  useCalendarAppointmentFormOptions,
  useEpisodeCareTeamProviders,
  useProviderCalendar,
  type ProviderCalendarWindow,
  calendarEventMatchesEocTypeFilter,
} from '../../features/calendar';
import { useCalendarPractitionerAccess } from '../../features/calendar/useCalendarPractitionerAccess';
import {
  AppointmentDateTimeFields,
  AppointmentLocationFields,
  AppointmentRecurrenceFields,
} from '../../features/calendar/AppointmentSharedFields';
import {
  SessionIdentityFields,
  type SessionAppointmentFieldsValue,
} from '../../features/calendar/SessionAppointmentFormFields';
import {
  additionalParticipantsForScheduleWrite,
  buildSessionAppointmentArgs,
  saveScheduledAppointmentWrite,
} from '../../features/calendar/sessionAppointmentArgs';
import {
  appointmentRequiresScheduleBot,
  assertAppointmentUnlockedForDirectWrite,
} from '../../features/calendar/signedNoteAppointmentLock';
import {
  attendeeLabelMap,
  buildEpisodeOptions,
  buildSessionAttendeeOptions,
  buildSessionProviderOptions,
  buildSessionTypeOptions,
  sessionTypeDurationMap,
} from '../../features/calendar/sessionSchedulerOptions';
import {
  readCalendarSelectionCookie,
  sanitizeCalendarSelection,
  writeCalendarSelectionCookie,
} from '../../features/calendar/calendarSelectionCookie';
import {
  assignPractitionerCalendarColors,
  isMultiPractitionerColorMode,
  type PractitionerCalendarColor,
} from '../../features/calendar/practitionerCalendarColors';
import {
  saveCalendarAppointment,
  deleteCalendarAppointment,
  isMedplumAppointmentId,
  MEETING_TYPE_SYSTEM,
  TIME_ENTRY_TYPE_SYSTEM,
  type AppointmentCategory,
} from '../../features/calendar/saveCalendarAppointment';
import { syncProviderAppointmentScheduleChange } from '../../features/calendar/providerAppointmentScheduleSync';
import {
  getDefaultProviderWeeklyAvailability,
  type ProviderWeeklyAvailability,
} from '../../features/calendar/providerAvailabilityTypes';
import {
  loadProviderWeeklyAvailability,
  saveProviderWeeklyAvailability,
} from '../../features/calendar/providerAvailabilitySchedule';
import {
  getProviderWeeklyAvailabilityUiDisableReason,
  mergeWeeklyAvailability,
} from '../../features/calendar/providerAvailabilityValidation';
import { WeeklyAvailabilityEditor } from '../../features/calendar/WeeklyAvailabilityEditor';
import { parseProviderWeeklyAvailabilityUi } from '../../schemas/schedule.schema';
import { getUnavailableRangesInHourFromWeekly } from '../../features/calendar/netAvailable';
import { DndProvider, useDrag, useDrop, useDragLayer } from 'react-dnd';
import { getEmptyImage, HTML5Backend } from 'react-dnd-html5-backend';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Divider,
  Drawer,
  Group,
  Menu,
  Modal,
  MultiSelect,
  Paper,
  Popover,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  appointmentFormSchema,
  defaultAppointmentFormValues,
  type AppointmentFormValues,
} from '../../schemas/appointment.schema';
import {
  createScheduledAppointment,
  updateScheduledAppointment,
} from '../../lib/scheduleAppointmentService';
import {
  collectSessionRecordingPatientIds,
  haveAllPatientsConsentedToRecording,
} from '../../lib/sessionRecordingConsentIndicator';
import { clientSelectDisplayName } from '../../lib/clientSelectOptions';
import { SessionRecordingConsentBadge } from '../../components/SessionRecordingConsentBadge';
import {
  emitCatalogChange,
  useCalendarPracticeId,
  useProviderMeetingTypes,
  useProviderSchedulableSessionTypes,
  useProviderTimeEntryTypes,
} from './appointmentTypeCatalog';
import {
  filterSessionTypesForEpisodeType,
  useEpisodeOfCareTypes,
} from './episodeOfCareTypeCatalog';
import { RoomAvailabilityDrawer, RoomBrowseDrawer } from './components/RoomAvailabilityDrawer';
import {
  GroupAppointmentScheduleFields,
  type GroupAppointmentScheduleFieldsValue,
} from './components/GroupAppointmentScheduleFields';
import { FilterMultiSelect } from '../../components/FilterMultiSelect';
import { ClientSelectOptionLabel } from '../../components/ClientSelectOptionLabel';
import {
  calendarEventReadOnlyMessage,
  isCalendarEventReadOnly,
  shouldShowCalendarJoin,
  viewerIsCalendarEventParticipant,
} from '../../features/calendar/calendarEventGuards';
import {
  applyZonedWallClock,
  applyZonedWallClockOnZonedDay,
  addZonedDays,
  addZonedMonths,
  calendarDateFromPickerValue,
  getZonedDateTimeParts,
  getZonedMonthGridDays,
  getZonedWeekDays,
  isSameZonedDay,
  startOfZonedDay,
  zonedWallClockToUtcDate,
} from '../../lib/dateHelpers';
import { useDisplayTimeZone } from '../../hooks/usePreferredDisplayTimeZone';
import { formatUsTimeZoneAbbreviation } from '../../lib/usTimeZoneLabel';
import {
  IconBan,
  IconBuilding,
  IconCalendarClock,
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconDeviceLaptop,
  IconDoor,
  IconFilter,
  IconPlus,
  IconSettings,
  IconUser,
  IconUsers,
  IconLock,
} from '@tabler/icons-react';

const BORDER_COLOR = 'var(--mantine-color-gray-5)';
const TODAY_BG = 'var(--mantine-color-gold-5)';

const CLIENT_STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled', color: 'var(--mantine-color-blue-6)' },
  { value: 'attended', label: 'Attended', color: 'var(--mantine-color-green-6)' },
  { value: 'client-cancelled', label: 'Client cancelled', color: 'var(--mantine-color-orange-6)' },
  { value: 'client-late-cancelled', label: 'Late cancelled', color: 'var(--mantine-color-orange-6)' },
  { value: 'client-no-show', label: 'No show', color: 'var(--mantine-color-red-6)' },
  /** Violet accent — same scale as design system Color page / Mantine theme (AI / proposed). */
  { value: 'proposed', label: 'Requested', color: 'var(--mantine-color-violet-6)' },
];

const CLIENT_STATUS_DOT_COLORS: Record<string, string> = Object.fromEntries(
  CLIENT_STATUS_OPTIONS.map((o) => [o.value, o.color])
);

/** Top-level event / group status dropdowns (subset includes clinician-cancelled). */
const TOP_LEVEL_EVENT_STATUS_SELECT_DATA: { value: string; label: string }[] = [
  { value: 'proposed', label: 'Proposed' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'attended', label: 'Attended' },
  { value: 'clinician-cancelled', label: 'Clinician cancelled' },
  { value: 'client-cancelled', label: 'Client cancelled' },
  { value: 'client-late-cancelled', label: 'Late cancelled' },
  { value: 'client-no-show', label: 'No show' },
];

const PIXELS_PER_HOUR = 80;
const DRAG_SNAP_MINUTES = 15;
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7 AM to 7 PM

/** Format timezone for calendar header e.g. "(MST)" */
function getTimeZoneShort(timeZone: string): string {
  const short = formatUsTimeZoneAbbreviation(timeZone);
  return short ? `(${short})` : '';
}

const PARTICIPANT_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType';
const CPT_SYSTEM = 'http://www.ama-assn.org/go/cpt';

/** Derive a short type label for calendar card display */
function getEventTypeLabel(event: CalendarEvent): string {
  if (event.type === 'blocked') return event.title;
  if (event.type === 'meeting') return 'Meeting';
  if (event.type === 'appointment') {
    if (event.title?.toLowerCase().includes('group')) return 'Group Session';
    return 'Individual Session';
  }
  return event.title;
}

/** Parse "9:00 AM" / "2:30 PM" to 24h hour and minute */
function parseTimeLabel(label: string): { hour: number; minute: number } | null {
  const match = label.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return { hour, minute };
}

/** Convert minutes-from-midnight to time label, snapped to 15 min (e.g. "9:00 AM") */
function minutesToTimeLabel(totalMinutes: number): string {
  const snapped = Math.round(totalMinutes / 15) * 15;
  const hour = Math.floor(snapped / 60) % 24;
  const minute = snapped % 60;
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const period = hour < 12 ? 'AM' : 'PM';
  return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
}

function parseResourceIdFromReference(
  reference: string | undefined,
  resourceType: string,
): string | undefined {
  if (!reference) return undefined;
  const marker = `${resourceType}/`;
  const idx = reference.lastIndexOf(marker);
  if (idx < 0) return undefined;
  const candidate = reference.slice(idx + marker.length).split(/[/?#]/)[0];
  return candidate || undefined;
}

function participantHasType(
  participant: AppointmentParticipant | undefined,
  system: string,
  code: string,
): boolean {
  return (
    participant?.type?.some((type) =>
      type.coding?.some((coding) => coding.system === system && coding.code === code),
    ) ?? false
  );
}

function findPrimaryPractitionerParticipant(
  participants: AppointmentParticipant[] | undefined,
): AppointmentParticipant | undefined {
  return (
    (participants ?? []).find((participant) =>
      participant.actor?.reference?.startsWith('Practitioner/')
      && participantHasType(participant, PARTICIPANT_TYPE_SYSTEM, 'PPRF'),
    )
    ?? (participants ?? []).find((participant) => participant.actor?.reference?.startsWith('Practitioner/'))
  );
}

function findPrimaryPatientParticipant(
  participants: AppointmentParticipant[] | undefined,
): AppointmentParticipant | undefined {
  return (
    (participants ?? []).find((participant) =>
      participant.actor?.reference?.startsWith('Patient/')
      && participantHasType(participant, PARTICIPANT_TYPE_SYSTEM, 'SBJ'),
    )
    ?? (participants ?? []).find((participant) => participant.actor?.reference?.startsWith('Patient/'))
  );
}

function getAppointmentCodeExtensionValue(
  appointment: Appointment,
  url: string,
): string | undefined {
  const value = appointment.extension?.find((extension) => extension.url === url)?.valueCode;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function getAppointmentUrlExtensionValue(
  appointment: Appointment,
  url: string,
): string | undefined {
  const value = appointment.extension?.find((extension) => extension.url === url)?.valueUrl;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function getAppointmentReferenceExtensionId(
  appointment: Appointment,
  url: string,
  resourceType: string,
): string | undefined {
  const reference = appointment.extension?.find((extension) => extension.url === url)?.valueReference?.reference;
  return parseResourceIdFromReference(reference, resourceType);
}

interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  start: Date;
  end: Date;
  type: 'appointment' | 'meeting' | 'task' | 'blocked';
  color: string;
  client?: string;
  /** Individual client IDs for group sessions */
  clients?: string[];
  /** Group type for group sessions (process, support, skills, psychoeducation, other) */
  groupType?: string;
  /** Per-client attendance statuses for group sessions */
  clientStatuses?: Record<string, string>;
  provider?: string;
  recurringPattern?: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly';
  videoLink?: string;
  /** For status-based card coloring (proposed=violet, scheduled=blue, late cancel/no-show=red struck, etc.) */
  status?: 'proposed' | 'scheduled' | 'attended' | 'clinician-cancelled' | 'client-cancelled' | 'client-late-cancelled' | 'client-no-show' | 'entered-in-error';
  /** True for blocked/time-entry items → gray card */
  isTimesheetItem?: boolean;
  modality?: 'in-person' | 'telehealth' | 'hybrid';
  siteId?: string;
  roomId?: string;
  /** Portable equipment reserved for this session (demo) */
  deviceIds?: string[];
  /** Appointment session type catalog ID (HealthcareService UUID) */
  sessionTypeId?: string;
  /** Display name for sessionTypeId when the viewer catalog omits that row. */
  sessionTypeDisplay?: string;
  /** Raw service type code from FHIR serviceType coding (for form round-tripping). */
  serviceTypeCode?: string;
  /** Additional participants beyond the primary practitioner and patient. */
  additionalParticipants?: Array<{ resourceType: string; id: string; display?: string }>;
  /** Calendar sub-category from FHIR serviceCategory (session/group/meeting/time-entry). */
  appointmentCategory?: 'session' | 'group' | 'meeting' | 'time-entry';
  /** Therapy cohort Group id linked to a Group session. */
  therapyGroupId?: string;
  /** Therapy cohort Group display name. */
  therapyGroupDisplay?: string;
  /** FHIR Patient id (no prefix). */
  patientId?: string;
  /** FHIR Practitioner id (no prefix). */
  practitionerId?: string;
  /** Canonical owner for multi-calendar color + authorization. */
  ownerPractitionerId?: string;
  /** Whether the current viewer may mutate this item. */
  canManage?: boolean;
  /** Patient details redacted to Busy. */
  isPrivacyRestricted?: boolean;
  /** EpisodeOfCare id from the episode-of-care-context extension. */
  episodeOfCareId?: string;
  /** `EpisodeOfCare.type` therapy-service-type slug (matches the EoC-type catalog `code`). */
  episodeOfCareTypeCode?: string;
  /** True when the current user is a participant but did not create the event (not PPRF). */
  isInvitedEvent?: boolean;
  /** True when a linked clinical note is signed or cosigned. */
  linkedNoteSigned?: boolean;
}

const DEFAULT_WEEKLY_AVAILABILITY = getDefaultProviderWeeklyAvailability();

function getEventColor(
  event: CalendarEvent,
  providerOverride?: PractitionerCalendarColor | null,
): { bg: string; border: string; text: string; strikethrough?: boolean } {
  const base = (() => {
    if (event.isTimesheetItem || event.type === 'blocked') {
      return {
        bg: 'var(--mantine-color-gray-1)',
        border: 'var(--mantine-color-gray-5)',
        text: 'var(--mantine-color-gray-7)',
      };
    }
    const s = event.status;
    if (s === 'client-cancelled' || s === 'client-late-cancelled' || s === 'client-no-show') {
      return {
        bg: 'var(--mantine-color-red-0)',
        border: 'var(--mantine-color-red-7)',
        text: 'var(--mantine-color-red-9)',
        strikethrough: true,
      };
    }
    if (s === 'clinician-cancelled') {
      return {
        bg: 'var(--mantine-color-yellow-0)',
        border: 'var(--mantine-color-yellow-6)',
        text: 'var(--mantine-color-yellow-9)',
        strikethrough: true,
      };
    }
    if (s === 'entered-in-error') {
      return {
        bg: 'var(--mantine-color-red-0)',
        border: 'var(--mantine-color-red-7)',
        text: 'var(--mantine-color-red-9)',
      };
    }
    if (s === 'attended') {
      return {
        bg: 'var(--mantine-color-green-0)',
        border: 'var(--mantine-color-green-6)',
        text: 'var(--mantine-color-green-9)',
      };
    }
    if (s === 'scheduled') {
      return {
        bg: 'var(--mantine-color-blue-0)',
        border: 'var(--mantine-color-blue-6)',
        text: 'var(--mantine-color-blue-9)',
      };
    }
    if (s === 'proposed') {
      return {
        bg: 'var(--mantine-color-violet-0)',
        border: 'var(--mantine-color-violet-6)',
        text: 'var(--mantine-color-violet-9)',
      };
    }
    const byType: Record<CalendarEvent['type'], { bg: string; border: string; text: string }> = {
      appointment: {
        bg: 'var(--mantine-color-blue-0)',
        border: 'var(--mantine-color-blue-6)',
        text: 'var(--mantine-color-blue-9)',
      },
      meeting: {
        bg: 'var(--mantine-color-blue-0)',
        border: 'var(--mantine-color-blue-6)',
        text: 'var(--mantine-color-blue-9)',
      },
      task: {
        bg: 'var(--mantine-color-blue-0)',
        border: 'var(--mantine-color-blue-6)',
        text: 'var(--mantine-color-blue-9)',
      },
      blocked: {
        bg: 'var(--mantine-color-gray-1)',
        border: 'var(--mantine-color-gray-5)',
        text: 'var(--mantine-color-gray-7)',
      },
    };
    return byType[event.type];
  })();

  if (!providerOverride) return base;
  return {
    ...base,
    bg: providerOverride.bg,
    border: providerOverride.border,
  };
}

function calendarEventAriaLabel(event: CalendarEvent): string {
  const provider = event.provider?.trim();
  const title = event.title?.trim() || getEventTypeLabel(event);
  return provider ? `${title} (${provider})` : title;
}

type CalendarView = 'month' | 'week' | 'day';

const CALENDAR_VIEW_OPTIONS: { value: CalendarView; label: string }[] = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

const EVENT_TYPE_COLOR_MAP: Record<CalendarEvent['type'], string> = {
  appointment: 'gold',
  meeting: 'green',
  task: 'blue',
  blocked: 'gray',
};

const CALENDAR_EVENT_DRAG_TYPE = 'calendar-event';

interface CalendarDragItem {
  event: CalendarEvent;
}

interface DraggableEventProps {
  event: CalendarEvent;
  onEdit: (event: CalendarEvent) => void;
  children: React.ReactNode;
}

function DraggableEvent({ event, onEdit, children }: DraggableEventProps): JSX.Element {
  const canDrag = event.canManage === true || (event.canManage === undefined && !event.isInvitedEvent);
  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: CALENDAR_EVENT_DRAG_TYPE,
    item: () => ({ event }),
    canDrag: () => canDrag,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });
  useEffect(() => {
    previewRef(getEmptyImage(), { captureDraggingState: true });
  }, [previewRef]);
  return (
    <div
      ref={dragRef}
      style={{
        height: '100%',
        cursor: canDrag ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        opacity: isDragging ? 0.5 : 1,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!isDragging) onEdit(event);
      }}
    >
      {children}
    </div>
  );
}

function getSnappedMinuteFromRelativePosition(relativeY: number, height: number): number {
  const slotCount = 60 / DRAG_SNAP_MINUTES;
  const pct = height > 0 ? relativeY / height : 0;
  const snappedSlot = Math.max(0, Math.min(slotCount - 1, Math.floor(pct * slotCount)));
  return snappedSlot * DRAG_SNAP_MINUTES;
}

const TOTAL_GRID_MINUTES = HOURS.length * 60;
const MAX_SNAPPED_MINUTES = TOTAL_GRID_MINUTES - DRAG_SNAP_MINUTES;

/** Global snap math shared by CalendarDragPreview and TimeGridDropZone. */
function getGlobalSnapPosition(
  clientOffset: { x: number; y: number } | null,
  initialClientOffset: { x: number; y: number } | null,
  initialSourceClientOffset: { x: number; y: number } | null,
  containerRect: DOMRect | null,
  numDays: number
): { leftPx: number; topPx: number; dayIndex: number; snappedMinutes: number } | null {
  if (!clientOffset || !initialClientOffset || !initialSourceClientOffset || !containerRect) return null;
  const grabOffsetY = initialClientOffset.y - initialSourceClientOffset.y;
  const eventTopPx = clientOffset.y - containerRect.top - grabOffsetY;
  const rawMinutes = eventTopPx * (60 / PIXELS_PER_HOUR);
  const snapped = Math.round(rawMinutes / DRAG_SNAP_MINUTES) * DRAG_SNAP_MINUTES;
  const snappedMinutes = Math.max(0, Math.min(MAX_SNAPPED_MINUTES, snapped));
  const topPx = snappedMinutes * (PIXELS_PER_HOUR / 60);

  const gridWidth = containerRect.width - 60;
  const colWidth = numDays > 0 ? gridWidth / numDays : 0;
  const xInGrid = clientOffset.x - containerRect.left - 60;
  const dayIndex = Math.max(0, Math.min(numDays - 1, Math.floor(colWidth > 0 ? xInGrid / colWidth : 0)));
  const leftPx = 60 + dayIndex * colWidth;

  return { leftPx, topPx, dayIndex, snappedMinutes };
}

interface CalendarDragPreviewProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  numDays: number;
}

function CalendarDragPreview({ containerRef, numDays }: CalendarDragPreviewProps): JSX.Element | null {
  const layerResult = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem<CalendarDragItem | null>(),
    currentOffset: monitor.getClientOffset(),
    initialClientOffset: monitor.getInitialClientOffset(),
    initialSourceClientOffset: monitor.getInitialSourceClientOffset(),
  }));

  if (!layerResult.isDragging || !layerResult.item || !layerResult.currentOffset) return null;
  const rect = containerRef.current?.getBoundingClientRect() ?? null;
  const snap = getGlobalSnapPosition(
    layerResult.currentOffset,
    layerResult.initialClientOffset,
    layerResult.initialSourceClientOffset,
    rect,
    numDays
  );
  if (!snap) return null;

  const { event } = layerResult.item;
  const durationMs = event.end.getTime() - event.start.getTime();
  const previewHeight = Math.max((durationMs / 3600000) * PIXELS_PER_HOUR, 8);
  const color = getEventColor(event);
  const previewHour = HOURS[0] + Math.floor(snap.snappedMinutes / 60);
  const previewMinute = snap.snappedMinutes % 60;
  const previewStartTime = new Date(2000, 0, 1, previewHour, previewMinute).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <div
      style={{
        position: 'absolute',
        left: snap.leftPx,
        top: snap.topPx,
        width: `calc((100% - 60px) / ${numDays} * 0.92)`,
        height: previewHeight,
        paddingLeft: 2,
        paddingRight: 2,
        pointerEvents: 'none',
        zIndex: 20,
        boxSizing: 'border-box',
      }}
    >
      <Card
        shadow="xs"
        padding="xs"
        withBorder
        style={{
          height: '100%',
          position: 'relative',
          backgroundColor: color.bg,
          borderColor: color.border,
          borderStyle: 'solid',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: 4, right: 4, lineHeight: 0, display: 'flex', gap: 2 }}>
          {event.isInvitedEvent && <IconLock size={12} color="var(--mantine-color-gray-6)" />}
          {event.modality && event.type !== 'blocked' && (
            event.modality === 'telehealth' ? (
              <IconDeviceLaptop size={14} color="var(--mantine-color-gray-6)" />
            ) : event.modality === 'hybrid' ? (
              <>
                <IconBuilding size={12} color="var(--mantine-color-gray-6)" />
                <IconDeviceLaptop size={12} color="var(--mantine-color-gray-6)" />
              </>
            ) : (
              <IconBuilding size={14} color="var(--mantine-color-gray-6)" />
            )
          )}
        </div>
        <Text size="xs" fw={500} lineClamp={1} c={color.text} style={{ paddingRight: (event.modality && event.type !== 'blocked') || event.isInvitedEvent ? (event.modality === 'hybrid' ? 42 : 28) : 0, ...(color.strikethrough ? { textDecoration: 'line-through' as const } : undefined) }}>
          {getEventTypeLabel(event)}
        </Text>
        <Text size="xs" c="gray.7" mt={2} style={color.strikethrough ? { textDecoration: 'line-through' as const } : undefined}>
          {previewStartTime}
        </Text>
        {previewHeight > 60 && event.client && (
          <Text size="xs" c="gray.6" lineClamp={1} mt={2} style={color.strikethrough ? { textDecoration: 'line-through' as const } : undefined}>
            {event.client}
          </Text>
        )}
        {event.type === 'blocked' && event.description && (
          <Text size="xs" c="gray.6" lineClamp={1} mt={2}>
            {event.description}
          </Text>
        )}
      </Card>
    </div>
  );
}

interface TimeGridDropZoneProps {
  weekDays: Date[];
  onEventMove: (eventId: string, newStart: Date, newEnd: Date) => void;
  onDropConsumed: () => void;
  timeZone: string;
  children: React.ReactNode;
}

function TimeGridDropZone({ weekDays, onEventMove, onDropConsumed, timeZone, children }: TimeGridDropZoneProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [{ isOver }, dropRef] = useDrop<CalendarDragItem, void, { isOver: boolean }>({
    accept: CALENDAR_EVENT_DRAG_TYPE,
    drop: (item, monitor) => {
      const { event } = item;
      const durationMs = event.end.getTime() - event.start.getTime();
      const clientOffset = monitor.getClientOffset();
      const initialClient = monitor.getInitialClientOffset();
      const initialSource = monitor.getInitialSourceClientOffset();
      const rect = containerRef.current?.getBoundingClientRect() ?? null;
      const snap = getGlobalSnapPosition(clientOffset, initialClient, initialSource, rect, weekDays.length);
      if (!snap) return;
      const day = weekDays[snap.dayIndex];
      const hour = HOURS[0] + Math.floor(snap.snappedMinutes / 60);
      const minute = snap.snappedMinutes % 60;
      const newStart = applyZonedWallClockOnZonedDay(day, hour, minute, timeZone);
      const newEnd = new Date(newStart.getTime() + durationMs);
      onEventMove(event.id, newStart, newEnd);
      onDropConsumed();
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });
  const setRefs = (el: HTMLDivElement | null) => {
    containerRef.current = el;
    dropRef(el);
  };
  return (
    <div
      ref={setRefs}
      style={{
        position: 'relative',
        backgroundColor: isOver ? 'rgba(201, 169, 97, 0.04)' : undefined,
      }}
    >
      {children}
      <CalendarDragPreview containerRef={containerRef} numDays={weekDays.length} />
    </div>
  );
}

function getEventLayout(dayEvents: CalendarEvent[]): Map<string, { column: number; totalColumns: number }> {
  const sorted = [...dayEvents].sort((x, y) => x.start.getTime() - y.start.getTime());
  const columns: CalendarEvent[][] = [];

  for (const event of sorted) {
    let placed = false;
    for (const col of columns) {
      const last = col[col.length - 1];
      if (event.start >= last.end) {
        col.push(event);
        placed = true;
        break;
      }
    }
    if (!placed) columns.push([event]);
  }

  const map = new Map<string, { column: number; totalColumns: number }>();
  for (const event of sorted) {
    let column = 0;
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].includes(event)) {
        column = i;
        break;
      }
    }
    let overlappingColumns = 0;
    for (const col of columns) {
      if (col.some((e) => e.start < event.end && e.end > event.start)) overlappingColumns++;
    }
    map.set(event.id, { column, totalColumns: overlappingColumns });
  }
  return map;
}

function timeToTop(hour: number, minute: number): number {
  return ((hour - 7) * 60 + minute) * (PIXELS_PER_HOUR / 60);
}

export function ProviderCalendarPage(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const practitioner = profile?.resourceType === 'Practitioner' ? (profile as Practitioner) : undefined;
  const orgId = useCalendarPracticeId(practitioner);
  const practitionerId = practitioner?.id ?? '';
  const displayTimeZone = useDisplayTimeZone();
  const {
    access: calendarAccess,
    accessById,
    canManageByOwnerId,
    showPractitionerSelector,
    currentPractitionerId: accessSelfId,
    loading: calendarAccessLoading,
    isUnifyAdmin: canScheduleAcrossPractices,
    sessionTypeScope,
  } = useCalendarPractitionerAccess();
  const appointmentSessionTypes = useProviderSchedulableSessionTypes(
    orgId,
    practitionerId,
    sessionTypeScope,
  );
  const meetingTypes = useProviderMeetingTypes(orgId, practitionerId);
  const timeEntryTypes = useProviderTimeEntryTypes(orgId, practitionerId);
  const meetingTypeSelectData = useMemo(
    () => meetingTypes.map((t) => ({ value: t.id, label: t.description })),
    [meetingTypes],
  );
  const timeEntryTypeSelectData = useMemo(
    () => timeEntryTypes.map((t) => ({ value: t.id, label: t.description })),
    [timeEntryTypes],
  );
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('week');
  const [calendarViewModalOpen, setCalendarViewModalOpen] = useState(false);
  const [calendarViewDraft, setCalendarViewDraft] = useState<CalendarView>('week');

  /** Visible calendar window (display-timezone day boundaries) for the Medplum query. */
  const calendarWindow = useMemo<ProviderCalendarWindow>(() => {
    if (view === 'day') {
      const start = startOfZonedDay(currentDate, displayTimeZone);
      const end = startOfZonedDay(addZonedDays(currentDate, 1, displayTimeZone), displayTimeZone);
      return { start, end };
    }
    if (view === 'week') {
      const weekDays = getZonedWeekDays(currentDate, displayTimeZone);
      return {
        start: weekDays[0],
        end: startOfZonedDay(addZonedDays(weekDays[0], 7, displayTimeZone), displayTimeZone),
      };
    }
    const monthDays = getZonedMonthGridDays(currentDate, displayTimeZone);
    return {
      start: monthDays[0],
      end: startOfZonedDay(addZonedDays(monthDays[0], 42, displayTimeZone), displayTimeZone),
    };
  }, [currentDate, view, displayTimeZone]);

  const [selectedPractitionerIds, setSelectedPractitionerIds] = useState<string[]>([]);
  const [selectionHydrated, setSelectionHydrated] = useState(false);
  const colorAssignmentRef = useRef<Map<string, PractitionerCalendarColor>>(new Map());

  useEffect(() => {
    if (calendarAccessLoading || !accessSelfId || calendarAccess.length === 0) return;
    const accessibleIds = calendarAccess.map((a) => a.practitionerId);
    if (!selectionHydrated) {
      const next = sanitizeCalendarSelection({
        saved: readCalendarSelectionCookie(),
        accessibleIds,
        currentPractitionerId: accessSelfId,
      });
      setSelectedPractitionerIds(next);
      setSelectionHydrated(true);
      return;
    }
    setSelectedPractitionerIds((prev) => {
      const filtered = prev.filter((id) => accessibleIds.includes(id));
      if (filtered.length === 0) return prev.length === 0 ? prev : [accessSelfId];
      if (filtered.length === prev.length && filtered.every((id, i) => id === prev[i])) return prev;
      writeCalendarSelectionCookie({ v: 1, ids: filtered });
      return filtered;
    });
  }, [calendarAccess, calendarAccessLoading, accessSelfId, selectionHydrated]);

  const handleSelectedPractitionerIdsChange = useCallback(
    (ids: string[]) => {
      const accessible = new Set(calendarAccess.map((a) => a.practitionerId));
      const next = ids.filter((id) => accessible.has(id));
      if (next.length === 0) {
        setSelectedPractitionerIds([]);
        writeCalendarSelectionCookie({ v: 1, ids: [] });
        return;
      }
      setSelectedPractitionerIds(next);
      const allSelected =
        calendarAccess.length > 0 && calendarAccess.every((a) => next.includes(a.practitionerId));
      writeCalendarSelectionCookie(
        allSelected
          ? { v: 1, ids: [], all: true }
          : { v: 1, ids: next },
      );
    },
    [calendarAccess],
  );

  const multiColorMode = isMultiPractitionerColorMode(selectedPractitionerIds.length);
  const practitionerColorMap = useMemo(() => {
    if (!accessSelfId || !multiColorMode) {
      colorAssignmentRef.current = new Map();
      return new Map<string, PractitionerCalendarColor>();
    }
    const next = assignPractitionerCalendarColors({
      selectedIds: selectedPractitionerIds,
      currentPractitionerId: accessSelfId,
      previousAssignment: colorAssignmentRef.current,
    });
    colorAssignmentRef.current = next;
    return next;
  }, [selectedPractitionerIds, accessSelfId, multiColorMode]);

  const selfDisplayName = useMemo(() => {
    if (!accessSelfId) return 'My Calendar';
    return accessById.get(accessSelfId)?.displayName ?? 'My Calendar';
  }, [accessSelfId, accessById]);

  const {
    events: liveEvents,
    error: liveError,
    hasResolvedPractitioner,
    practitionerId: livePractitionerId,
    refresh: refreshLiveEvents,
  } = useProviderCalendar({
    window: calendarWindow,
    selectedPractitionerIds: selectionHydrated ? selectedPractitionerIds : accessSelfId ? [accessSelfId] : [],
    currentProviderDisplayLabel: selfDisplayName,
    canManageByOwnerId,
  });

  const eventDisplayColor = useCallback(
    (event: CalendarEvent) => {
      const ownerId = event.ownerPractitionerId ?? event.practitionerId;
      const override =
        multiColorMode && ownerId ? practitionerColorMap.get(ownerId) ?? null : null;
      return getEventColor(event, override);
    },
    [multiColorMode, practitionerColorMap],
  );

  // ── Practice locations / rooms / equipment from FHIR ────────────────
  /** Unify admins schedule across practices, so their own practice must not scope the site list. */
  const { sites, roomsBySiteId, equipmentBySiteId } = useLocationResources({
    organizationId: orgId,
    crossOrganization: canScheduleAcrossPractices,
  });
  const siteOptions = useMemo(
    () => sites.map((s) => ({ value: s.id, label: [s.name, s.addressLine1].filter(Boolean).join(' — ') })),
    [sites],
  );
  const firstSiteId = sites[0]?.id ?? '';
  const appointmentForm = useForm<AppointmentFormValues>({
    initialValues: defaultAppointmentFormValues(),
    validate: zod4Resolver(appointmentFormSchema),
  });

  const [events, setEvents] = useState<CalendarEvent[]>([]);

  /** When live appointments arrive, replace local state. */
  useEffect(() => {
    setEvents(liveEvents as CalendarEvent[]);
  }, [liveEvents]);

  // Seed location state once real sites are available.
  useEffect(() => {
    if (firstSiteId && !appointmentForm.values.siteId) {
      appointmentForm.setFieldValue('siteId', firstSiteId);
    }
    if (firstSiteId && !appointmentForm.values.officeForTelehealth) {
      appointmentForm.setFieldValue('officeForTelehealth', firstSiteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSiteId]);

  const [modalOpened, setModalOpened] = useState(false);
  const [calendarCancelling, setCalendarCancelling] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [editorAppointment, setEditorAppointment] = useState<Appointment | undefined>(undefined);

  useEffect(() => {
    if (!modalOpened || !selectedEvent?.id || !isMedplumAppointmentId(selectedEvent.id)) {
      setEditorAppointment(undefined);
      return;
    }
    let cancelled = false;
    void medplum.readResource('Appointment', selectedEvent.id, { cache: 'no-cache' })
      .then((appt) => {
        if (!cancelled) setEditorAppointment(appt);
      })
      .catch(() => {
        if (!cancelled) setEditorAppointment(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, modalOpened, selectedEvent?.id]);

  /** `?eventId=` can open the editor before calendar access resolves; refresh the
   * open event's permission flags so it isn't stranded read-only. */
  useEffect(() => {
    setSelectedEvent((prev) => {
      if (!prev) return prev;
      const fresh = events.find((e) => e.id === prev.id);
      if (!fresh || fresh.canManage === prev.canManage) return prev;
      return { ...prev, canManage: fresh.canManage, isInvitedEvent: fresh.isInvitedEvent };
    });
  }, [events]);
  /** Status when the edit modal opened — drives disabled attendance transition options. */
  const [savedCalendarAttendanceStatus, setSavedCalendarAttendanceStatus] = useState<string>('scheduled');
  const newEventDate = appointmentForm.values.date;
  const newEventStartTime = appointmentForm.values.startTime;
  const newEventEndTime = appointmentForm.values.endTime;
  const newEventTitle = appointmentForm.values.title;
  const newEventDescription = appointmentForm.values.description;
  const newEventType = appointmentForm.values.eventType;
  const newEventAppointmentType = appointmentForm.values.appointmentCategory;
  const newEventSessionType = appointmentForm.values.sessionType;
  const newEventGroupType = appointmentForm.values.groupType;
  const newEventTherapyGroupId = appointmentForm.values.therapyGroupId;
  const newEventModality = appointmentForm.values.modality;
  const newEventLocation = appointmentForm.values.siteId;
  const newEventAttendanceStatus = appointmentForm.values.attendanceStatus;
  const newEventProvider = appointmentForm.values.provider;
  const newEventFacilitator = appointmentForm.values.facilitator;
  const newEventCoFacilitators = appointmentForm.values.coFacilitators;
  const [showOtherCoFacilitators, setShowOtherCoFacilitators] = useState(false);
  const newEventClient = appointmentForm.values.client;
  const newEventSessionAttendees = appointmentForm.values.sessionAttendees;
  const newEventClients = appointmentForm.values.clients;
  const newEventClientStatuses = appointmentForm.values.clientStatuses;
  const newEventMeetingType = appointmentForm.values.meetingType;
  const newEventMeetingHost = appointmentForm.values.meetingHost;
  const newEventMeetingParticipants = appointmentForm.values.meetingParticipants;
  const newEventMeetingDescription = appointmentForm.values.meetingDescription;
  const newEventTimeEntryType = appointmentForm.values.timeEntryType;
  const newEventTimeEntryNotes = appointmentForm.values.timeEntryNotes;
  const newEventRecurring = appointmentForm.values.recurring;
  const newEventVideoLink = appointmentForm.values.videoLink;
  const newEventRoom = appointmentForm.values.roomId;
  const newEventDevices = appointmentForm.values.deviceIds;
  const newEventTelehealthFromOffice = appointmentForm.values.telehealthFromOffice;
  const newEventOfficeForTelehealth = appointmentForm.values.officeForTelehealth;
  const [roomAvailDrawerOpen, setRoomAvailDrawerOpen] = useState(false);
  const [roomBrowseDrawerOpen, setRoomBrowseDrawerOpen] = useState(false);
  const newEventEpisodeOfCare = appointmentForm.values.episodeOfCareId;

  /** For sessions the `provider` form value is the selected provider's Practitioner id.
   * Session-type options follow the viewer's catalog scope (provider allowlist,
   * practice-enabled list, or platform catalog). */
  const sessionProviderId = newEventAppointmentType === 'session' ? newEventProvider : '';
  const sessionProviderSessionTypes = useProviderSchedulableSessionTypes(
    orgId,
    sessionProviderId || practitionerId,
    sessionTypeScope,
  );

  const setNewEventDate = (value: Date | null) => appointmentForm.setFieldValue('date', value);
  const setNewEventStartTime = (value: string) => appointmentForm.setFieldValue('startTime', value);
  const setNewEventEndTime = (value: string) => appointmentForm.setFieldValue('endTime', value);
  const setNewEventType = (value: AppointmentFormValues['eventType']) => appointmentForm.setFieldValue('eventType', value);
  const setNewEventAppointmentType = (value: AppointmentFormValues['appointmentCategory']) => appointmentForm.setFieldValue('appointmentCategory', value);
  const setNewEventSessionType = (value: string) => {
    appointmentForm.setFieldValue('sessionType', value);
    const duration = sessionProviderSessionTypes.find((t) => t.id === value)?.durationMinutes;
    if (duration) {
      const parsed = parseTimeLabel(appointmentForm.values.startTime);
      if (parsed) {
        appointmentForm.setFieldValue('endTime', minutesToTimeLabel(parsed.hour * 60 + parsed.minute + duration));
      }
    }
  };
  /** Bridges the shared field blocks' patch API onto the Mantine form. */
  const applySessionFieldPatch = (patch: Partial<SessionAppointmentFieldsValue>) => {
    for (const [field, value] of Object.entries(patch)) {
      appointmentForm.setFieldValue(field as keyof AppointmentFormValues, value as never);
    }
  };
  const sessionTypeDurations = useMemo(
    () => sessionTypeDurationMap(sessionProviderSessionTypes),
    [sessionProviderSessionTypes],
  );
  const setNewEventTherapyGroupId = (value: string) => appointmentForm.setFieldValue('therapyGroupId', value);

  const [therapyGroups, setTherapyGroups] = useState<FhirGroup[]>([]);
  const [therapyGroupsLoading, setTherapyGroupsLoading] = useState(false);

  useEffect(() => {
    if (!modalOpened || newEventAppointmentType !== 'group') return;
    let cancelled = false;
    setTherapyGroupsLoading(true);
    /** Unify admins schedule across practices, so their own practice must not scope the cohort list. */
    void searchTherapyGroups(medplum, {
      organizationId: canScheduleAcrossPractices ? undefined : orgId || undefined,
      activeOnly: true,
    })
      .then((groups) => {
        if (!cancelled) setTherapyGroups(groups);
      })
      .catch(() => {
        if (!cancelled) setTherapyGroups([]);
      })
      .finally(() => {
        if (!cancelled) setTherapyGroupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpened, newEventAppointmentType, medplum, orgId, canScheduleAcrossPractices]);

  const therapyGroupSelectData = useMemo(() => {
    const options = therapyGroups
      .filter((g) => g.id)
      .map((g) => ({ value: g.id!, label: g.name?.trim() || `Group ${g.id}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
    /** Keep an existing session's cohort renderable even when it is no longer active. */
    const linkedId = selectedEvent?.therapyGroupId;
    if (linkedId && !options.some((o) => o.value === linkedId)) {
      options.unshift({
        value: linkedId,
        label: selectedEvent?.therapyGroupDisplay?.trim() || `Group ${linkedId}`,
      });
    }
    return options;
  }, [therapyGroups, selectedEvent?.therapyGroupId, selectedEvent?.therapyGroupDisplay]);
  const setNewEventModality = (value: AppointmentFormValues['modality']) => appointmentForm.setFieldValue('modality', value);
  const setNewEventLocation = (value: string | ((prev: string) => string)) =>
    appointmentForm.setFieldValue('siteId', typeof value === 'function' ? value(appointmentForm.values.siteId) : value);
  const setNewEventAttendanceStatus = (value: AppointmentFormValues['attendanceStatus']) =>
    appointmentForm.setFieldValue('attendanceStatus', value);

  const handleAttendanceStatusChange = (value: string | null) => {
    const next = (value ?? 'scheduled') as AppointmentFormValues['attendanceStatus'];
    if (getCalendarStatusTransitionDisabledReason(savedCalendarAttendanceStatus, next)) return;
    setNewEventAttendanceStatus(next);
  };

  const topLevelEventStatusSelectData = useMemo(
    () => buildCalendarStatusSelectData(savedCalendarAttendanceStatus, TOP_LEVEL_EVENT_STATUS_SELECT_DATA),
    [savedCalendarAttendanceStatus],
  );
  const setNewEventProvider = (value: string) => appointmentForm.setFieldValue('provider', value);
  const setNewEventFacilitator = (value: string) => appointmentForm.setFieldValue('facilitator', value);
  const setNewEventCoFacilitators = (value: string[]) => appointmentForm.setFieldValue('coFacilitators', value);
  const setNewEventClients = (value: string[] | ((prev: string[]) => string[])) =>
    appointmentForm.setFieldValue('clients', typeof value === 'function' ? value(appointmentForm.values.clients) : value);
  const setNewEventClientStatuses = (
    value: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)
  ) => appointmentForm.setFieldValue(
    'clientStatuses',
    typeof value === 'function' ? value(appointmentForm.values.clientStatuses) : value,
  );

  const setNewEventMeetingType = (value: string) => {
    appointmentForm.setFieldValue('meetingType', value);
    const duration = meetingTypes.find((t) => t.description === value)?.defaultDurationMinutes;
    if (duration) {
      const parsed = parseTimeLabel(appointmentForm.values.startTime);
      if (parsed) {
        appointmentForm.setFieldValue('endTime', minutesToTimeLabel(parsed.hour * 60 + parsed.minute + duration));
      }
    }
  };
  const setNewEventMeetingHost = (value: string) => appointmentForm.setFieldValue('meetingHost', value);
  const setNewEventMeetingParticipants = (value: string[]) => appointmentForm.setFieldValue('meetingParticipants', value);
  const setNewEventMeetingDescription = (value: string) => appointmentForm.setFieldValue('meetingDescription', value);
  const setNewEventTimeEntryType = (value: string) => {
    appointmentForm.setFieldValue('timeEntryType', value);
    const duration = timeEntryTypes.find((t) => t.description === value)?.defaultDurationMinutes;
    if (duration) {
      const parsed = parseTimeLabel(appointmentForm.values.startTime);
      if (parsed) {
        appointmentForm.setFieldValue('endTime', minutesToTimeLabel(parsed.hour * 60 + parsed.minute + duration));
      }
    }
  };
  const setNewEventTimeEntryNotes = (value: string) => appointmentForm.setFieldValue('timeEntryNotes', value);
  const setNewEventRoom = (value: string | null) => appointmentForm.setFieldValue('roomId', value);
  const setNewEventDevices = (value: string[]) => appointmentForm.setFieldValue('deviceIds', value);
  const setNewEventTelehealthFromOffice = (value: boolean) => appointmentForm.setFieldValue('telehealthFromOffice', value);
  const setNewEventOfficeForTelehealth = (value: string) => appointmentForm.setFieldValue('officeForTelehealth', value);

  /** Live: Medplum-backed provider / client / EoC (primary careManager = logged-in user) options for the create modal. */
  const calendarFormOptions = useCalendarAppointmentFormOptions(
    modalOpened,
    newEventProvider || accessSelfId,
  );
  const providerSelectData = calendarFormOptions.providerSelectData;
  const allProviderSelectData = calendarFormOptions.allProviderSelectData;
  const clientSelectData = calendarFormOptions.clientSelectData;

  const applyTherapyGroupPrefill = useCallback(
    (group: FhirGroup | undefined) => {
      if (!group) {
        setNewEventClients([]);
        return;
      }
      const patientIds = getActiveGroupMemberPatientIds(group);
      void (async () => {
        const clientKeys: string[] = [];
        for (const patientId of patientIds) {
          const fromCaseload = calendarFormOptions.liveEpisodeDetails.find(
            (e) => e.patientId === patientId,
          );
          if (fromCaseload?.patientId) {
            clientKeys.push(fromCaseload.patientId);
            continue;
          }
          const onSelect = clientSelectData.find((d) => d.value === patientId);
          if (onSelect) {
            clientKeys.push(patientId);
            continue;
          }
          const member = group.member?.find((m) => m.entity?.reference === `Patient/${patientId}`);
          const memberDisplay = member?.entity?.display?.trim();
          if (memberDisplay) {
            clientKeys.push(memberDisplay);
            continue;
          }
          try {
            const patient = (await medplum.readResource('Patient', patientId)) as Patient;
            const name =
              (patient.name?.[0] ? formatHumanName(patient.name[0]) : undefined)?.trim() ||
              patient.name?.[0]?.text?.trim();
            clientKeys.push(name || patientId);
          } catch {
            clientKeys.push(patientId);
          }
        }
        setNewEventClients(clientKeys);
        const statuses: Record<string, string> = {};
        for (const key of clientKeys) statuses[key] = 'scheduled';
        setNewEventClientStatuses(statuses);
      })();

      /** Template's session HealthcareService drives serviceType + note template on one-off sessions. */
      const templateRef = getGroupTemplateRef(group)?.reference?.replace(
        /^HealthcareService\//,
        '',
      );
      if (templateRef) {
        void (async () => {
          try {
            const template = await medplum.readResource('HealthcareService', templateRef);
            const sessionHsId = getGroupTemplateSessionHealthcareService(template)
              ?.reference?.replace(/^HealthcareService\//, '');
            if (sessionHsId) {
              appointmentForm.setFieldValue('sessionType', sessionHsId);
            }
          } catch {
            // Template may be missing; leave session type unset.
          }
        })();
      }

      const facilitators = getGroupFacilitators(group);
      const primaryRef = facilitators[0]?.reference;
      const primaryId = primaryRef?.startsWith('Practitioner/')
        ? primaryRef.slice('Practitioner/'.length)
        : primaryRef;
      if (primaryId) {
        setNewEventProvider(primaryId);
        setNewEventFacilitator(
          calendarFormOptions.practitionerDisplayById.get(primaryId) ?? primaryId,
        );
      }
      const coIds = facilitators
        .slice(1)
        .map((r) => r.reference?.replace(/^Practitioner\//, ''))
        .filter((id): id is string => !!id);
      if (coIds.length) {
        setShowOtherCoFacilitators(true);
        setNewEventCoFacilitators(
          coIds.map((id) => calendarFormOptions.practitionerDisplayById.get(id) ?? id),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calendarFormOptions.liveEpisodeDetails, calendarFormOptions.practitionerDisplayById, clientSelectData, medplum],
  );

  /** Selected Case row + the care-team-scoped Provider options it drives. */
  const selectedEpisodeDetail = useMemo(
    () => calendarFormOptions.liveEpisodeDetails.find((e) => e.id === newEventEpisodeOfCare) ?? null,
    [calendarFormOptions.liveEpisodeDetails, newEventEpisodeOfCare],
  );
  const episodeCareTeamProviders = useEpisodeCareTeamProviders(
    selectedEpisodeDetail,
    calendarFormOptions.practitionerDisplayById,
  );
  const sessionProviderSelectData = useMemo(
    () =>
      buildSessionProviderOptions({
        canManageByOwnerId,
        selectedProviderId: newEventProvider,
        practitionerDisplayById: calendarFormOptions.practitionerDisplayById,
        fallbackLabel: selectedEvent?.provider,
      }),
    [
      canManageByOwnerId,
      newEventProvider,
      calendarFormOptions.practitionerDisplayById,
      selectedEvent,
    ],
  );
  /** Display name for the session's selected provider (form stores the Practitioner id). */
  const sessionProviderDisplay = useMemo(() => {
    if (!newEventProvider) return undefined;
    return (
      episodeCareTeamProviders.options.find((o) => o.value === newEventProvider)?.label ??
      calendarFormOptions.practitionerDisplayById.get(newEventProvider) ??
      (isHumanParticipantDisplay(selectedEvent?.provider, newEventProvider)
        ? selectedEvent?.provider
        : undefined)
    );
  }, [
    episodeCareTeamProviders.options,
    newEventProvider,
    calendarFormOptions.practitionerDisplayById,
    selectedEvent,
  ]);

  /** Session attendee options: the Case's client plus the episode's `participants`
   * extension members (RelatedPerson). Values are full refs (`Patient/..`, `RelatedPerson/..`). */
  const sessionAttendeeOptions = useMemo(
    () =>
      buildSessionAttendeeOptions({
        episode: selectedEpisodeDetail,
        fallback: selectedEvent
          ? {
            patientId: selectedEvent.patientId,
            clientDisplay: selectedEvent.client,
            relatedPersons: (selectedEvent.additionalParticipants ?? []).filter(
              (p) => p.resourceType === 'RelatedPerson',
            ),
          }
          : undefined,
        selectedRefs: newEventSessionAttendees,
      }),
    [selectedEpisodeDetail, selectedEvent, newEventSessionAttendees],
  );

  /** Patients whose session-recording consent gates Zoom auto-recording for this session. */
  const sessionRecordingPatientIds = useMemo(
    () =>
      newEventAppointmentType === 'session'
        ? collectSessionRecordingPatientIds({
          primaryPatientId: selectedEpisodeDetail?.patientId ?? selectedEvent?.patientId,
          attendeeRefs: newEventSessionAttendees,
        })
        : [],
    [newEventAppointmentType, selectedEpisodeDetail?.patientId, selectedEvent?.patientId, newEventSessionAttendees],
  );
  /** null while loading / not applicable; otherwise "all clients permitted recording". */
  const [sessionRecordingConsented, setSessionRecordingConsented] = useState<boolean | null>(null);
  useEffect(() => {
    setSessionRecordingConsented(null);
    if (!modalOpened || sessionRecordingPatientIds.length === 0) return undefined;
    let cancelled = false;
    void haveAllPatientsConsentedToRecording(medplum, sessionRecordingPatientIds).then((allPermitted) => {
      if (!cancelled) setSessionRecordingConsented(allPermitted);
    });
    return () => {
      cancelled = true;
    };
  }, [modalOpened, sessionRecordingPatientIds, medplum]);

  /** EoC-type catalog entry for the selected Case — drives the approved-session filter. */
  const { types: episodeOfCareTypes } = useEpisodeOfCareTypes();
  const eocTypeFilterOptions = useMemo(
    () =>
      episodeOfCareTypes
        .filter((type) => type.active && type.code)
        .map((type) => ({ value: type.code, label: type.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [episodeOfCareTypes],
  );
  const selectedEpisodeEocType = useMemo(
    () => {
      const code = selectedEpisodeDetail?.typeCode ?? selectedEvent?.episodeOfCareTypeCode;
      return episodeOfCareTypes.find((t) => t.code && t.code === code);
    },
    [episodeOfCareTypes, selectedEpisodeDetail, selectedEvent?.episodeOfCareTypeCode],
  );

  const calendarModalTitle = useMemo(() => {
    if (newEventAppointmentType === 'meeting') return 'Meeting';
    if (newEventAppointmentType === 'time-entry') return 'Time Entry';
    if (newEventAppointmentType === 'group') {
      const groupName =
        therapyGroupSelectData.find((o) => o.value === newEventTherapyGroupId)?.label
        ?? selectedEvent?.therapyGroupDisplay
        ?? undefined;
      return buildAppointmentDisplayTitle({
        groupDisplayName: groupName,
        fallback: 'Group Session',
      });
    }
    if (newEventAppointmentType === 'session') {
      return resolveSessionAppointmentTitle({
        episodeDisplayName: selectedEpisodeDetail?.episodeDisplayName,
        episodeTypeName: selectedEpisodeEocType?.name,
        sessionTypeDisplay: selectedEvent?.sessionTypeDisplay,
        existingTitle: selectedEvent?.title,
        clientDisplayName:
          selectedEpisodeDetail?.clientName
          ?? newEventClient
          ?? selectedEvent?.client
          ?? undefined,
        fallback: selectedEvent ? 'Session' : 'New Event',
      });
    }
    return 'New Event';
  }, [
    newEventAppointmentType,
    newEventTherapyGroupId,
    therapyGroupSelectData,
    selectedEvent?.therapyGroupDisplay,
    selectedEvent?.client,
    selectedEvent?.sessionTypeDisplay,
    selectedEvent?.title,
    selectedEpisodeDetail?.episodeDisplayName,
    selectedEpisodeDetail?.clientName,
    selectedEpisodeEocType?.name,
    newEventClient,
    selectedEvent,
  ]);

  /** Provider-schedulable types restricted to the Case's approved set (fails open
   * for unknown episode types or unconfigured approved sets). Drives the Select
   * options and new-event defaults only — id-based lookups (duration, CPT, title)
   * stay on the unfiltered `sessionProviderSessionTypes`. */
  const episodeFilteredSessionTypes = useMemo(
    () => filterSessionTypesForEpisodeType(sessionProviderSessionTypes, selectedEpisodeEocType),
    [sessionProviderSessionTypes, selectedEpisodeEocType],
  );
  const sessionTypeSelectData = useMemo(
    () =>
      buildSessionTypeOptions({
        episodeFilteredTypes: episodeFilteredSessionTypes,
        allProviderTypes: sessionProviderSessionTypes,
        selectedSessionTypeId: newEventSessionType,
        editing: Boolean(selectedEvent),
        fallbackLabel: selectedEvent?.sessionTypeDisplay,
      }),
    [episodeFilteredSessionTypes, selectedEvent, newEventSessionType, sessionProviderSessionTypes],
  );

  /** Group sessions are not episode-scoped: offer the full schedulable catalog. */
  const groupSessionTypeSelectData = useMemo(
    () => {
      const data = appointmentSessionTypes.map((t) => ({
        value: t.id,
        label: [
          t.description,
          `${t.durationMinutes} min`,
          t.cptCode || undefined,
        ].filter(Boolean).join(' - '),
      }));
      const current = newEventSessionType;
      if (selectedEvent && current && !data.some((o) => o.value === current)) {
        data.push({
          value: current,
          label: selectedEvent.sessionTypeDisplay?.trim() || current,
        });
      }
      return data;
    },
    [appointmentSessionTypes, selectedEvent, newEventSessionType],
  );

  /** Default session type once a Case is picked and the catalogs load — prefers the
   * episode type's STANDARD session type when schedulable, else the first option.
   * New events use the EoC-filtered list; edits keep the stored session type even
   * when the viewer's catalog does not include it. */
  useEffect(() => {
    if (!modalOpened || newEventAppointmentType !== 'session') return;
    if (!newEventEpisodeOfCare && !selectedEvent) return;
    const membershipList = selectedEvent ? sessionProviderSessionTypes : episodeFilteredSessionTypes;
    if (membershipList.length === 0) return;
    const current = appointmentForm.values.sessionType;
    if (current && membershipList.some((t) => t.id === current)) return;
    if (selectedEvent && current) return;
    const standardId = selectedEpisodeEocType?.standardSessionTypeId;
    const preferred =
      standardId && membershipList.some((t) => t.id === standardId)
        ? standardId
        : membershipList[0].id;
    appointmentForm.setFieldValue('sessionType', preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpened, newEventAppointmentType, newEventEpisodeOfCare, selectedEvent, sessionProviderSessionTypes, episodeFilteredSessionTypes, selectedEpisodeEocType]);

  const showRecurringOptions = appointmentForm.values.recurringEnabled;
  const recurringInterval = appointmentForm.values.recurringInterval;
  const recurringTimeframe = appointmentForm.values.recurringTimeframe;
  const recurringDaysOfWeek = appointmentForm.values.recurringDaysOfWeek;
  const recurringEndType = appointmentForm.values.recurringEndType;
  const recurringEndDate = appointmentForm.values.recurringEndDate;
  const recurringOccurrences = appointmentForm.values.recurringOccurrences;
  const setShowRecurringOptions = (value: boolean) => appointmentForm.setFieldValue('recurringEnabled', value);
  const setRecurringInterval = (value: number) => appointmentForm.setFieldValue('recurringInterval', value);
  const setRecurringTimeframe = (value: string) => appointmentForm.setFieldValue('recurringTimeframe', value);
  const setRecurringDaysOfWeek = (value: string[]) => appointmentForm.setFieldValue('recurringDaysOfWeek', value);
  const setRecurringEndType = (value: 'occurrences' | 'date') => appointmentForm.setFieldValue('recurringEndType', value);
  const setRecurringEndDate = (value: Date | null) => appointmentForm.setFieldValue('recurringEndDate', value);
  const setRecurringOccurrences = (value: number) => appointmentForm.setFieldValue('recurringOccurrences', value);
  const [recurringActionModalOpened, setRecurringActionModalOpened] = useState(false);
  const [recurringActionType, setRecurringActionType] = useState<'edit' | 'delete' | 'move'>('edit');
  const [pendingDragMove, setPendingDragMove] = useState<{ eventId: string; newStart: Date; newEnd: Date } | null>(null);
  const [eventTypeFilter, setEventTypeFilter] = useState<string[]>(['appointment', 'meeting', 'task', 'blocked']);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [eocTypeFilter, setEocTypeFilter] = useState<string[]>([]);
  const eocTypeFilterSeeded = useRef(false);
  useEffect(() => {
    if (eocTypeFilterSeeded.current || eocTypeFilterOptions.length === 0) return;
    setEocTypeFilter(eocTypeFilterOptions.map((o) => o.value));
    eocTypeFilterSeeded.current = true;
  }, [eocTypeFilterOptions]);
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);
  const [weeklyAvailability, setWeeklyAvailability] = useState<ProviderWeeklyAvailability>(() => ({
    ...DEFAULT_WEEKLY_AVAILABILITY,
  }));
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [availabilityDraft, setAvailabilityDraft] = useState<ProviderWeeklyAvailability>(() => ({
    ...DEFAULT_WEEKLY_AVAILABILITY,
  }));
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilitySaving, setAvailabilitySaving] = useState(false);

  useEffect(() => {
    if (!livePractitionerId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { weekly } = await loadProviderWeeklyAvailability(medplum, livePractitionerId);
        if (!cancelled) {
          setWeeklyAvailability(mergeWeeklyAvailability(weekly));
        }
      } catch {
        // keep defaults when Schedule is missing or load fails
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, livePractitionerId]);

  const practitionerSelectorData = useMemo(
    () =>
      calendarAccess.map((a) => ({
        value: a.practitionerId,
        label: a.displayName,
      })),
    [calendarAccess],
  );

  const manageablePractitionerSelectData = useMemo(
    () =>
      calendarAccess
        .filter((a) => a.canManage)
        .map((a) => ({ value: a.practitionerId, label: a.displayName })),
    [calendarAccess],
  );

  const episodeOfCareOptions = useMemo(
    () =>
      buildEpisodeOptions({
        episodeSelectData: calendarFormOptions.episodeSelectData,
        episodeDetails: calendarFormOptions.liveEpisodeDetails,
        selectedEpisodeId: newEventEpisodeOfCare,
        fallbackLabel: selectedEvent?.client,
      }),
    [
      calendarFormOptions.episodeSelectData,
      calendarFormOptions.liveEpisodeDetails,
      newEventEpisodeOfCare,
      selectedEvent,
    ],
  );

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const dropJustHappenedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open event edit modal when navigated with ?eventId=...
  useEffect(() => {
    const eventId = searchParams.get('eventId');
    if (!eventId) return;
    const match = events.find((e) => e.id === eventId);
    if (match) {
      openEditEventModal(match);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, events]);

  // Deep-link: /dashboard/calendar?groupId=...&appointmentCategory=group
  useEffect(() => {
    const groupId = searchParams.get('groupId');
    const category = searchParams.get('appointmentCategory');
    if (!groupId || searchParams.get('eventId')) return;
    if (category && category !== 'group') return;
    openNewEventModal();
    setNewEventAppointmentType('group');
    setNewEventType('appointment');
    setShowRecurringOptions(false);
    appointmentForm.setFieldValue('recurring', 'none');
    appointmentForm.setFieldValue('recurringEnabled', false);
    setNewEventTherapyGroupId(groupId);
    setSearchParams({}, { replace: true });
    // Prefill once therapy groups load (separate effect below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!modalOpened || newEventAppointmentType !== 'group' || !newEventTherapyGroupId) return;
    const group = therapyGroups.find((g) => g.id === newEventTherapyGroupId);
    if (group) applyTherapyGroupPrefill(group);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapyGroups, newEventTherapyGroupId, modalOpened, newEventAppointmentType]);

  const weekDays = getZonedWeekDays(currentDate, displayTimeZone);
  const monthDays = getZonedMonthGridDays(currentDate, displayTimeZone);

  const roomHighlightRange = useMemo(() => {
    if (!newEventDate) return { start: null as Date | null, end: null as Date | null };
    const s = parseTimeLabel(newEventStartTime);
    const e = parseTimeLabel(newEventEndTime);
    if (!s || !e) return { start: null, end: null };
    const start = applyZonedWallClock(newEventDate, s.hour, s.minute, displayTimeZone);
    const end = applyZonedWallClock(newEventDate, e.hour, e.minute, displayTimeZone);
    if (end <= start) return { start: null, end: null };
    return { start, end };
  }, [newEventDate, newEventStartTime, newEventEndTime, displayTimeZone]);

  const roomDrawerLocationId =
    newEventModality === 'telehealth' && newEventTelehealthFromOffice
      ? newEventOfficeForTelehealth
      : newEventLocation !== 'telehealth'
        ? newEventLocation
        : 'main-office';

  const schedulingSiteId =
    newEventModality === 'telehealth'
      ? (newEventTelehealthFromOffice ? newEventOfficeForTelehealth : '')
      : (newEventLocation !== 'telehealth' ? newEventLocation : '');

  const schedulingRooms = useMemo(
    () => (roomsBySiteId[schedulingSiteId] ?? []).filter((room) => room.status === 'active'),
    [roomsBySiteId, schedulingSiteId],
  );
  const schedulingPortableDevices = useMemo(
    () =>
      (equipmentBySiteId[schedulingSiteId] ?? []).filter(
        (equipment) => equipment.portability === 'portable' && equipment.status === 'active',
      ),
    [equipmentBySiteId, schedulingSiteId],
  );
  const fixedInSelectedRoom = useMemo(
    () =>
      (equipmentBySiteId[schedulingSiteId] ?? []).filter(
        (equipment) =>
          equipment.portability === 'fixed'
          && equipment.homeRoomId === newEventRoom
          && equipment.status === 'active',
      ),
    [equipmentBySiteId, schedulingSiteId, newEventRoom],
  );

  const availabilityExcludeAppointmentId =
    selectedEvent?.id && isMedplumAppointmentId(selectedEvent.id)
      ? selectedEvent.id
      : undefined;

  const {
    busyByRoom,
    busyByDevice,
  } = useRoomAvailabilityForDate({
    date: newEventDate,
    roomIds: schedulingRooms.map((room) => room.id),
    deviceIds: schedulingPortableDevices.map((device) => device.id),
    excludeAppointmentId: availabilityExcludeAppointmentId,
    refreshKey: modalOpened,
  });

  const handleEventMove = async (eventId: string, newStart: Date, newEnd: Date) => {
    const event = events.find((e) => e.id === eventId);
    if (event && isCalendarEventReadOnly(event)) {
      notifications.show({
        title: 'View only',
        message: calendarEventReadOnlyMessage(event),
        color: 'red',
      });
      return;
    }
    if (event?.recurringPattern && event.recurringPattern !== 'none') {
      setPendingDragMove({ eventId, newStart, newEnd });
      setRecurringActionType('move');
      setRecurringActionModalOpened(true);
      return;
    }

    if (event && isMedplumAppointmentId(eventId)) {
      const moved = await persistMovedLiveEvent(event, newStart, newEnd);
      if (!moved) return;
    }

    setEvents((prev) =>
      prev.map((e) => (e.id === eventId ? { ...e, start: newStart, end: newEnd } : e))
    );
    notifications.show({
      title: 'Event moved',
      message: 'Event has been rescheduled',
      color: 'green',
    });
  };

  const performDragMove = async (scope: 'single' | 'all') => {
    if (!pendingDragMove) return;
    const { eventId, newStart, newEnd } = pendingDragMove;
    const event = events.find((e) => e.id === eventId);
    if (!event) return;

    if (isMedplumAppointmentId(eventId)) {
      const moved = await persistMovedLiveEvent(event, newStart, newEnd);
      if (!moved) {
        setPendingDragMove(null);
        return;
      }
    }

    if (scope === 'single') {
      setEvents((prev) =>
        prev.map((e) => (e.id === eventId ? { ...e, start: newStart, end: newEnd } : e))
      );
      notifications.show({ title: 'Event moved', message: 'This instance has been rescheduled', color: 'green' });
    } else {
      const deltaMs = newStart.getTime() - event.start.getTime();
      setEvents((prev) =>
        prev.map((e) => {
          const isThisOrFuture =
            e.id === eventId ||
            (e.recurringPattern &&
              e.recurringPattern !== 'none' &&
              e.title === event.title &&
              e.start.getTime() >= event.start.getTime());
          if (!isThisOrFuture) return e;
          return {
            ...e,
            start: new Date(e.start.getTime() + deltaMs),
            end: new Date(e.end.getTime() + deltaMs),
          };
        })
      );
      notifications.show({ title: 'Events moved', message: 'All future instances have been rescheduled', color: 'green' });
    }
    setPendingDragMove(null);
    setRecurringActionModalOpened(false);
  };

  const getEventsForDay = (day: Date) =>
    events
      .filter((e) => {
        if (!isSameZonedDay(e.start, day, displayTimeZone) || !eventTypeFilter.includes(e.type)) return false;
        if (statusFilter.length > 0 && e.status && !statusFilter.includes(e.status)) return false;
        if (statusFilter.length > 0 && !e.status && e.type !== 'blocked') return false; // no status = hide if filtering by status
        if (!calendarEventMatchesEocTypeFilter(e, eocTypeFilter)) return false;
        return true;
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());

  /** Unavailable minute-ranges within the given hour (0–60). Used to shade only the unavailable portion of an hour cell. */
  const getUnavailableRangesInHour = useCallback(
    (day: Date, hour: number) => getUnavailableRangesInHourFromWeekly(day, hour, weeklyAvailability),
    [weeklyAvailability],
  );

  const getEventPosition = (event: CalendarEvent) => {
    const startParts = getZonedDateTimeParts(event.start, displayTimeZone);
    const endParts = getZonedDateTimeParts(event.end, displayTimeZone);
    const top = timeToTop(startParts.hour, startParts.minute);
    const endTop = timeToTop(endParts.hour, endParts.minute);
    return { top, height: endTop - top };
  };

  const getCurrentTimePosition = () => {
    const now = getZonedDateTimeParts(new Date(), displayTimeZone);
    return timeToTop(now.hour, now.minute);
  };

  const navigateCalendar = (dir: 'prev' | 'next') => {
    const delta = dir === 'next' ? 1 : -1;
    if (view === 'day') setCurrentDate(addZonedDays(currentDate, delta, displayTimeZone));
    else if (view === 'week') setCurrentDate(addZonedDays(currentDate, delta * 7, displayTimeZone));
    else setCurrentDate(addZonedMonths(currentDate, delta, displayTimeZone));
  };

  const openNewEventModal = (date?: Date, hour?: number, minute?: number) => {
    setSelectedEvent(null);
    const d = date ?? (() => {
      const parts = getZonedDateTimeParts(new Date(), displayTimeZone);
      return new Date(parts.year, parts.month - 1, parts.day);
    })();
    const min = minute ?? 0;
    let startTime = '9:00 AM';
    let endTime = '10:00 AM';
    if (hour !== undefined) {
      const h12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const period = hour >= 12 ? 'PM' : 'AM';
      startTime = `${h12}:${min.toString().padStart(2, '0')} ${period}`;
      const endMin = min + 60;
      const endHour = hour + Math.floor(endMin / 60);
      const endMinOnly = endMin % 60;
      const eh12 = endHour > 12 ? endHour - 12 : endHour === 0 ? 12 : endHour;
      const ePeriod = endHour >= 12 ? 'PM' : 'AM';
      endTime = `${eh12}:${endMinOnly.toString().padStart(2, '0')} ${ePeriod}`;
    }
    const defaultProvName =
      calendarFormOptions.defaultPrimaryProviderLabel || selfDisplayName;
    const manageableSelected = selectedPractitionerIds.filter(
      (id) => canManageByOwnerId.get(id) === true,
    );
    const defaultTargetPractitionerId =
      manageableSelected.length === 1
        ? manageableSelected[0]
        : livePractitionerId || accessSelfId || '';
    appointmentForm.setValues(defaultAppointmentFormValues({
      date: d,
      startTime,
      endTime,
      modality: 'telehealth',
      siteId: 'telehealth',
      /** Session provider (Practitioner id) starts empty and is defaulted from the
       * selected Case's careManager; facilitator/host keep display-name defaults. */
      facilitator: defaultProvName,
      meetingHost: defaultProvName,
      provider: defaultTargetPractitionerId,
      officeForTelehealth: firstSiteId,
    }));
    appointmentForm.clearErrors();
    setShowOtherCoFacilitators(false);
    setRoomAvailDrawerOpen(false);
    emitCatalogChange();
    setModalOpened(true);
  };

  const openEditEventModal = (event: CalendarEvent) => {
    setSelectedEvent(event);
    const initialAttendanceStatus =
      event.status === 'entered-in-error' ? 'scheduled' : (event.status ?? 'scheduled');
    setSavedCalendarAttendanceStatus(initialAttendanceStatus);
    const startParts = getZonedDateTimeParts(event.start, displayTimeZone);
    const endParts = getZonedDateTimeParts(event.end, displayTimeZone);
    const sh = startParts.hour;
    const sm = startParts.minute;
    const eh = endParts.hour;
    const em = endParts.minute;
    const sh12 = sh > 12 ? sh - 12 : sh === 0 ? 12 : sh;
    const eh12 = eh > 12 ? eh - 12 : eh === 0 ? 12 : eh;
    const startTime = `${sh12}:${sm.toString().padStart(2, '0')} ${sh >= 12 ? 'PM' : 'AM'}`;
    const endTime = `${eh12}:${em.toString().padStart(2, '0')} ${eh >= 12 ? 'PM' : 'AM'}`;
    const category = event.appointmentCategory
      ?? (event.type === 'blocked' ? 'time-entry'
        : event.type === 'meeting' ? 'meeting'
          : 'session') as CalendarEvent['appointmentCategory'];
    /** Group sessions keep every co-facilitator as an extra Practitioner participant. */
    const coFacilitatorIds = category === 'group'
      ? (event.additionalParticipants ?? [])
        .filter((p) => p.resourceType === 'Practitioner' && p.id !== event.practitionerId)
        .map((p) => p.id)
      : [];
    appointmentForm.setValues(defaultAppointmentFormValues({
      appointmentCategory: category ?? 'session',
      eventType: event.type,
      date: new Date(startParts.year, startParts.month - 1, startParts.day),
      startTime,
      endTime,
      title: event.title,
      description: event.description ?? '',
      sessionType: event.sessionTypeId ?? '',
      groupType: event.groupType ?? '',
      program: 'outpatient',
      modality: event.modality ?? 'in-person',
      siteId: event.modality === 'telehealth' ? 'telehealth' : (event.siteId ?? firstSiteId),
      attendanceStatus: initialAttendanceStatus,
      /** Session and group `provider` carry the Practitioner id; other categories keep display names. */
      provider: category === 'session' || category === 'group'
        ? (event.practitionerId ?? '')
        : (event.provider ?? ''),
      episodeOfCareId: event.episodeOfCareId ?? null,
      facilitator: event.provider ?? '',
      therapyGroupId: event.therapyGroupId ?? '',
      coFacilitators: coFacilitatorIds,
      client: event.client ?? null,
      sessionAttendees: category === 'session'
        ? [
          ...(event.patientId ? [`Patient/${event.patientId}`] : []),
          ...(event.additionalParticipants ?? [])
            .filter((p) => p.resourceType === 'RelatedPerson')
            .map((p) => `RelatedPerson/${p.id}`),
        ]
        : [],
      clients: event.clients ?? [],
      clientStatuses: event.clientStatuses ?? {},
      meetingType: category === 'meeting'
        ? (meetingTypes.find((t) => t.id === event.serviceTypeCode)?.id
          || meetingTypes.find((t) => t.description === event.serviceTypeCode)?.id
          || '')
        : '',
      meetingHost: category === 'meeting'
        ? (providerSelectData.some((p) => p.value === event.provider)
          ? event.provider!
          : calendarFormOptions.defaultPrimaryProviderLabel || event.provider || '')
        : '',
      meetingParticipants: category === 'meeting' && event.additionalParticipants
        ? event.additionalParticipants
          .filter((p) => p.resourceType === 'Practitioner')
          .map((p) => p.id)
        : [],
      meetingDescription: category === 'meeting' ? (event.description ?? '') : '',
      timeEntryType: category === 'time-entry'
        ? (timeEntryTypes.find((t) => t.id === event.serviceTypeCode)?.id
          || timeEntryTypes.find((t) => t.description === event.serviceTypeCode)?.id
          || '')
        : '',
      timeEntryNotes: category === 'time-entry' ? (event.description ?? '') : '',
      recurring: event.recurringPattern ?? 'none',
      recurringEnabled: (event.recurringPattern ?? 'none') !== 'none',
      videoLink: event.videoLink ?? '',
      roomId: event.roomId ?? null,
      deviceIds: event.deviceIds ?? [],
      telehealthFromOffice: Boolean(event.modality === 'telehealth' && event.roomId && event.siteId),
      officeForTelehealth: event.siteId ?? firstSiteId,
    }));
    appointmentForm.clearErrors();
    setShowOtherCoFacilitators(coFacilitatorIds.length > 0);
    setRoomAvailDrawerOpen(false);
    emitCatalogChange();
    setModalOpened(true);
  };

  const getEffectiveTitle = () =>
    newEventTitle ||
    (newEventAppointmentType === 'session'
      ? (sessionProviderSessionTypes.find((t) => t.id === newEventSessionType)?.description || 'Session')
      : newEventAppointmentType === 'group'
        ? 'Group Session'
        : newEventAppointmentType === 'meeting'
          ? (newEventMeetingType || 'Meeting')
          : newEventAppointmentType === 'time-entry'
            ? (newEventTimeEntryType || '')
            : '');

  const getSaveErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object' && 'outcome' in err) {
      const issue = (err as {
        outcome?: { issue?: Array<{ diagnostics?: string; details?: { text?: string } }> };
      }).outcome?.issue?.[0];
      if (issue?.diagnostics) return issue.diagnostics;
      if (issue?.details?.text) return issue.details.text;
    }
    return err instanceof Error ? err.message : String(err);
  };

  const persistMovedLiveEvent = async (
    event: CalendarEvent,
    newStart: Date,
    newEnd: Date,
  ): Promise<boolean> => {
    const hasScheduledResources = Boolean(event.roomId) || (event.deviceIds?.length ?? 0) > 0;

    try {
      const existing = await medplum.readResource('Appointment', event.id);
      const before = existing;
      const practitionerParticipant = findPrimaryPractitionerParticipant(existing.participant);
      const practitionerId =
        parseResourceIdFromReference(practitionerParticipant?.actor?.reference, 'Practitioner')
        || event.practitionerId
        || livePractitionerId
        || undefined;
      const bookingForOther = Boolean(
        practitionerId && livePractitionerId && practitionerId !== livePractitionerId,
      );

      if (hasScheduledResources || bookingForOther) {
        const patientParticipant = findPrimaryPatientParticipant(existing.participant);
        const serviceType = existing.serviceType?.[0];
        const serviceCoding = serviceType?.coding?.[0];
        const appointmentCategory = event.appointmentCategory;
        const serviceTypeDisplay =
          serviceCoding?.display?.trim()
          || serviceType?.text?.trim()
          || event.sessionTypeId
          || undefined;
        const usesStructuredServiceType = Boolean(
          serviceCoding?.code && serviceCoding?.system && serviceCoding.system !== CPT_SYSTEM,
        );
        const fallbackServiceTypeSystem =
          appointmentCategory === 'meeting'
            ? MEETING_TYPE_SYSTEM
            : appointmentCategory === 'time-entry'
              ? TIME_ENTRY_TYPE_SYSTEM
              : undefined;

        if (!practitionerId) {
          throw new Error('Could not determine the practitioner for this appointment');
        }

        await updateScheduledAppointment(medplum, event.id, {
          start: newStart,
          end: newEnd,
          calendarStatus: event.status ?? 'scheduled',
          organizationId: orgId ?? undefined,
          practitionerId,
          practitionerDisplay:
            calendarFormOptions.practitionerPreferredDisplayById.get(practitionerId),
          siteId: event.siteId,
          roomId: event.roomId,
          deviceIds: event.deviceIds,
          patientId: parseResourceIdFromReference(patientParticipant?.actor?.reference, 'Patient') || event.patientId || undefined,
          patientDisplay: patientParticipant?.actor?.display?.trim() || event.client || undefined,
          episodeOfCareId: getAppointmentReferenceExtensionId(existing, EPISODE_OF_CARE_CONTEXT_EXT, 'EpisodeOfCare'),
          cptCode: usesStructuredServiceType
            ? undefined
            : (serviceCoding?.code?.trim() || (!fallbackServiceTypeSystem ? event.serviceTypeCode : undefined) || undefined),
          cptDisplay: usesStructuredServiceType ? undefined : serviceTypeDisplay,
          serviceTypeCode: usesStructuredServiceType
            ? (serviceCoding?.code?.trim() || event.serviceTypeCode || undefined)
            : (fallbackServiceTypeSystem ? (serviceCoding?.code?.trim() || event.serviceTypeCode || undefined) : undefined),
          serviceTypeDisplay: usesStructuredServiceType || fallbackServiceTypeSystem
            ? serviceTypeDisplay
            : undefined,
          serviceTypeSystem: usesStructuredServiceType
            ? serviceCoding?.system?.trim()
            : fallbackServiceTypeSystem,
          appointmentCategory: appointmentCategory as AppointmentCategory | undefined,
          modality: event.modality ?? 'in-person',
          description: existing.description?.trim() || event.description || undefined,
          videoLink: getAppointmentUrlExtensionValue(existing, VIDEO_MEETING_URL_EXT) || event.videoLink || undefined,
          recurringPattern: getAppointmentCodeExtensionValue(existing, RECURRING_PATTERN_EXT) || event.recurringPattern || undefined,
          additionalParticipants: additionalParticipantsForScheduleWrite(
            event.additionalParticipants,
            calendarFormOptions.practitionerPreferredDisplayById,
          ),
        });
      } else {
        // Direct PUT — the bot cannot enforce the signed-note lock here, and the calendar's
        // cached lock flag may be stale, so re-check against the notes before writing.
        await assertAppointmentUnlockedForDirectWrite(
          medplum,
          event.id,
          livePractitionerId || practitionerId || undefined,
        );
        await medplum.updateResource({
          ...existing,
          start: newStart.toISOString(),
          end: newEnd.toISOString(),
          minutesDuration: Math.round((newEnd.getTime() - newStart.getTime()) / 60_000),
        });
      }

      const after = await medplum.readResource('Appointment', event.id);
      try {
        await syncProviderAppointmentScheduleChange(medplum, {
          before,
          after,
          calendarStatus: event.status ?? 'scheduled',
        });
      } catch (syncErr) {
        console.warn('[ProviderCalendar] Appointment schedule sync failed:', syncErr);
      }

      refreshLiveEvents();
      return true;
    } catch (err) {
      const errorMessage = getSaveErrorMessage(err);
      notifications.show({
        title: hasScheduledResources && /is booked/i.test(errorMessage) ? 'Time Unavailable' : 'Move Failed',
        message: hasScheduledResources && /is booked/i.test(errorMessage)
          ? `${errorMessage}. Open the appointment modal to see room availability.`
          : `Could not reschedule: ${errorMessage}`,
        color: 'red',
      });
      return false;
    }
  };

  const performSaveEvent = async (scope: 'single' | 'all') => {
    if (selectedEvent && isCalendarEventReadOnly(selectedEvent)) {
      notifications.show({
        title: 'View only',
        message: calendarEventReadOnlyMessage(selectedEvent),
        color: 'red',
      });
      return;
    }
    const effectiveTitle = getEffectiveTitle();
    if (!newEventDate || !effectiveTitle) return;
    const startParsed = parseTimeLabel(newEventStartTime);
    const endParsed = parseTimeLabel(newEventEndTime);
    if (!startParsed || !endParsed) return;
    const start = applyZonedWallClock(newEventDate, startParsed.hour, startParsed.minute, displayTimeZone);
    const end = applyZonedWallClock(newEventDate, endParsed.hour, endParsed.minute, displayTimeZone);
    if (end <= start) return;

    const siteIdToSave =
      newEventModality === 'telehealth'
        ? (newEventTelehealthFromOffice ? newEventOfficeForTelehealth || undefined : undefined)
        : (newEventLocation !== 'telehealth' ? newEventLocation || undefined : undefined);
    const roomIdToSave =
      newEventModality === 'telehealth'
        ? (newEventTelehealthFromOffice ? newEventRoom ?? undefined : undefined)
        : (newEventLocation !== 'telehealth' ? newEventRoom ?? undefined : undefined);
    const deviceIdsToSave =
      (newEventModality === 'in-person' || newEventModality === 'hybrid') &&
        newEventLocation !== 'telehealth' &&
        newEventDevices.length > 0
        ? [...newEventDevices]
        : undefined;
    const hasExistingScheduledResources =
      Boolean(selectedEvent?.roomId) || (selectedEvent?.deviceIds?.length ?? 0) > 0;

    if (livePractitionerId) {
      const existingFhirId =
        selectedEvent?.id && isMedplumAppointmentId(selectedEvent.id)
          ? selectedEvent.id
          : undefined;

      let patientId: string | undefined;
      let patientDisplay: string | undefined;
      let episodeOfCareId: string | undefined;
      let cptCode: string | undefined;
      let cptDisplay: string | undefined;
      let primaryPractitionerId = livePractitionerId;
      let primaryPractitionerDisplay: string | undefined;
      const additionalParticipants: Array<{
        resourceType: 'Practitioner' | 'Patient' | 'RelatedPerson';
        id: string;
        display?: string;
      }> = [];
      const effectiveDescription =
        newEventAppointmentType === 'meeting'
          ? (newEventMeetingDescription || newEventDescription)
          : newEventAppointmentType === 'time-entry'
            ? (newEventTimeEntryNotes || newEventDescription)
            : newEventDescription;

      if (newEventAppointmentType === 'session') {
        /** Shared with the in-session scheduler so both surfaces write identical args. */
        const sessionWrite = buildSessionAppointmentArgs(
          { ...appointmentForm.values, recurring: newEventRecurring },
          {
            viewerPractitionerId: livePractitionerId,
            viewerPreferredDisplay:
              calendarFormOptions.practitionerPreferredDisplayById.get(livePractitionerId),
            organizationId: orgId ?? undefined,
            patientId: selectedEpisodeDetail?.patientId ?? selectedEvent?.patientId,
            patientDisplay: selectedEpisodeDetail?.clientName ?? newEventClient ?? undefined,
            providerPreferredDisplay: calendarFormOptions.practitionerPreferredDisplayById.get(
              appointmentForm.values.provider,
            ),
            sessionTypes: sessionProviderSessionTypes,
            attendeeLabelByRef: attendeeLabelMap(sessionAttendeeOptions),
            hasExistingScheduledResources,
          },
        );
        if (!sessionWrite) return;

        if (canManageByOwnerId.get(sessionWrite.args.practitionerId) !== true) {
          notifications.show({
            title: 'Not permitted',
            message: 'You do not have permission to manage this practitioner’s calendar.',
            color: 'red',
          });
          return;
        }

        try {
          await saveScheduledAppointmentWrite(medplum, sessionWrite, {
            appointmentId: existingFhirId,
            viewerPractitionerId: livePractitionerId,
          });
          refreshLiveEvents();
          notifications.show({
            title: selectedEvent ? 'Event Updated' : 'Event Created',
            message: selectedEvent
              ? `${effectiveTitle} has been updated`
              : `${effectiveTitle} on ${newEventDate.toLocaleDateString('en-US')}`,
            color: 'green',
          });
          setRecurringActionModalOpened(false);
          setModalOpened(false);
        } catch (err) {
          notifications.show({
            title: 'Save Failed',
            message: `Could not save appointment: ${getSaveErrorMessage(err)}`,
            color: 'red',
          });
        }
        return;
      }

      if (newEventAppointmentType === 'group') {
        if (newEventProvider) {
          primaryPractitionerId = newEventProvider;
        }
        primaryPractitionerDisplay =
          calendarFormOptions.practitionerPreferredDisplayById.get(primaryPractitionerId);
        const sessionType = appointmentSessionTypes.find((t) => t.id === newEventSessionType);
        cptCode = sessionType?.cptCode;
        cptDisplay = sessionType?.billingTitle;
        const selectedGroup = therapyGroups.find((g) => g.id === newEventTherapyGroupId);
        const groupPatientIds = selectedGroup
          ? new Set(getActiveGroupMemberPatientIds(selectedGroup))
          : new Set<string>();
        for (const clientKey of newEventClients) {
          const byPatientId = calendarFormOptions.liveEpisodeDetails.find(
            (e) => e.patientId === clientKey,
          );
          if (byPatientId?.patientId) {
            additionalParticipants.push({
              resourceType: 'Patient',
              id: byPatientId.patientId,
              display: byPatientId.clientName,
            });
            continue;
          }
          const selectLabel = clientSelectData.find((d) => d.value === clientKey)?.label;
          if (selectLabel) {
            additionalParticipants.push({
              resourceType: 'Patient',
              id: clientKey,
              display: clientSelectDisplayName(selectLabel),
            });
            continue;
          }
          const byName = calendarFormOptions.liveEpisodeDetails.find(
            (e) => e.clientName === clientKey,
          );
          if (byName?.patientId) {
            additionalParticipants.push({
              resourceType: 'Patient',
              id: byName.patientId,
              display: clientKey,
            });
            continue;
          }
          // Prefill may use Group member display or raw Patient id when not on caseload.
          if (groupPatientIds.has(clientKey)) {
            additionalParticipants.push({ resourceType: 'Patient', id: clientKey });
            continue;
          }
          const member = selectedGroup?.member?.find(
            (m) =>
              m.entity?.display === clientKey
              || m.entity?.reference === `Patient/${clientKey}`,
          );
          const memberId = member?.entity?.reference?.replace(/^Patient\//, '');
          if (memberId) {
            additionalParticipants.push({
              resourceType: 'Patient',
              id: memberId,
              display: member?.entity?.display,
            });
          }
        }
        for (const co of newEventCoFacilitators) {
          const coId =
            calendarFormOptions.allProviderSelectData.find((p) => p.value === co || p.label === co)
              ?.value
            ?? (co.startsWith('Practitioner/') ? co.slice('Practitioner/'.length) : undefined);
          if (!coId || coId === primaryPractitionerId) continue;
          const display = calendarFormOptions.practitionerPreferredDisplayById.get(coId);
          additionalParticipants.push({
            resourceType: 'Practitioner',
            id: coId,
            ...(display ? { display } : {}),
          });
        }
      } else if (newEventAppointmentType === 'meeting') {
        if (newEventProvider && canManageByOwnerId.get(newEventProvider)) {
          primaryPractitionerId = newEventProvider;
        }
        primaryPractitionerDisplay =
          calendarFormOptions.practitionerPreferredDisplayById.get(primaryPractitionerId);
        for (const pId of newEventMeetingParticipants) {
          const display = calendarFormOptions.practitionerPreferredDisplayById.get(pId);
          additionalParticipants.push({
            resourceType: 'Practitioner',
            id: pId,
            ...(display ? { display } : {}),
          });
        }
      } else if (newEventAppointmentType === 'time-entry') {
        if (newEventProvider && canManageByOwnerId.get(newEventProvider)) {
          primaryPractitionerId = newEventProvider;
        }
        primaryPractitionerDisplay =
          calendarFormOptions.practitionerPreferredDisplayById.get(primaryPractitionerId);
      }

      if (canManageByOwnerId.get(primaryPractitionerId) !== true) {
        notifications.show({
          title: 'Not permitted',
          message: 'You do not have permission to manage this practitioner’s calendar.',
          color: 'red',
        });
        return;
      }

      let sessionHealthcareServiceId: string | undefined;
      let sessionHealthcareServiceDisplay: string | undefined;
      let serviceTypeCode: string | undefined;
      let serviceTypeDisplay: string | undefined;
      let serviceTypeSystem: string | undefined;
      if (newEventAppointmentType === 'group' && newEventSessionType) {
        sessionHealthcareServiceId = newEventSessionType;
        sessionHealthcareServiceDisplay = appointmentSessionTypes.find(
          (type) => type.id === newEventSessionType,
        )?.description;
      }
      if (newEventAppointmentType === 'group') {
        // Match the generated-series shape so note templates resolve for one-off sessions.
        serviceTypeCode = 'group-therapy';
        serviceTypeDisplay = 'Group Therapy';
        serviceTypeSystem = THERAPY_SERVICE_TYPE_SYSTEM;
      } else if (newEventAppointmentType === 'meeting' && newEventMeetingType) {
        const mt = meetingTypes.find((t) => t.id === newEventMeetingType);
        serviceTypeCode = newEventMeetingType;
        serviceTypeDisplay = mt?.description;
        serviceTypeSystem = MEETING_TYPE_SYSTEM;
      } else if (newEventAppointmentType === 'time-entry' && newEventTimeEntryType) {
        const tet = timeEntryTypes.find((t) => t.id === newEventTimeEntryType);
        serviceTypeCode = newEventTimeEntryType;
        serviceTypeDisplay = tet?.description;
        serviceTypeSystem = TIME_ENTRY_TYPE_SYSTEM;
      }

      try {
        const beforeAppointment = existingFhirId
          ? await medplum.readResource('Appointment', existingFhirId)
          : undefined;

        const saveArgs = {
          start,
          end,
          calendarStatus: newEventAttendanceStatus || 'scheduled',
          practitionerId: primaryPractitionerId,
          practitionerDisplay: primaryPractitionerDisplay,
          siteId: siteIdToSave,
          organizationId: orgId ?? undefined,
          roomId: roomIdToSave,
          deviceIds: deviceIdsToSave,
          patientId,
          patientDisplay,
          episodeOfCareId,
          cptCode,
          cptDisplay,
          sessionHealthcareServiceId,
          sessionHealthcareServiceDisplay,
          serviceTypeCode,
          serviceTypeDisplay,
          serviceTypeSystem,
          appointmentCategory: newEventAppointmentType as AppointmentCategory,
          modality: newEventModality || 'in-person',
          description: effectiveDescription || undefined,
          videoLink: newEventVideoLink || undefined,
          recurringPattern:
            newEventAppointmentType === 'group' ? 'none' : (newEventRecurring || undefined),
          additionalParticipants: additionalParticipants.length > 0 ? additionalParticipants : undefined,
          therapyGroupId:
            newEventAppointmentType === 'group' ? (newEventTherapyGroupId || undefined) : undefined,
          therapyGroupDisplay:
            newEventAppointmentType === 'group'
              ? (therapyGroups.find((g) => g.id === newEventTherapyGroupId)?.name ?? undefined)
              : undefined,
        };
        const shouldUseScheduledBot =
          Boolean(roomIdToSave)
          || (deviceIdsToSave?.length ?? 0) > 0
          || hasExistingScheduledResources
          || (existingFhirId ? await appointmentRequiresScheduleBot(medplum, existingFhirId) : false);

        if (shouldUseScheduledBot) {
          if (existingFhirId) {
            await updateScheduledAppointment(medplum, existingFhirId, saveArgs);
          } else {
            await createScheduledAppointment(medplum, saveArgs);
          }
        } else {
          await saveCalendarAppointment({
            medplum,
            appointmentId: existingFhirId,
            ...saveArgs,
          });
        }

        if (beforeAppointment && existingFhirId && patientId) {
          try {
            const afterAppointment = await medplum.readResource('Appointment', existingFhirId);
            await syncProviderAppointmentScheduleChange(medplum, {
              before: beforeAppointment,
              after: afterAppointment,
              calendarStatus: newEventAttendanceStatus || 'scheduled',
            });
          } catch (syncErr) {
            console.warn('[ProviderCalendar] Appointment schedule sync failed:', syncErr);
          }
        }

        if (newEventAppointmentType === 'group' && newEventTherapyGroupId) {
          try {
            await requestAppointmentSeriesSyncParticipants(medplum, newEventTherapyGroupId);
          } catch (syncErr) {
            console.warn('[ProviderCalendar] Group participant sync failed:', syncErr);
            notifications.show({
              title: 'Group participants not synced',
              message:
                'Appointment saved, but updating Group session participants failed. Open the group and sync members if needed.',
              color: 'yellow',
            });
          }
        }

        refreshLiveEvents();
        notifications.show({
          title: selectedEvent ? 'Event Updated' : 'Event Created',
          message: selectedEvent
            ? `${effectiveTitle} has been updated`
            : `${effectiveTitle} on ${newEventDate.toLocaleDateString('en-US')}`,
          color: 'green',
        });
        setRecurringActionModalOpened(false);
        setModalOpened(false);
        return;
      } catch (err) {
        notifications.show({
          title: 'Save Failed',
          message: `Could not save appointment: ${getSaveErrorMessage(err)}`,
          color: 'red',
        });
        return;
      }
    }

    const color = EVENT_TYPE_COLOR_MAP[newEventType as CalendarEvent['type']];
    const effectiveProvider =
      newEventAppointmentType === 'group'
        ? newEventFacilitator || sessionProviderDisplay
        : newEventAppointmentType === 'meeting'
          ? newEventMeetingHost
          || (newEventProvider
            ? calendarFormOptions.practitionerDisplayById.get(newEventProvider)
            : undefined)
          : sessionProviderDisplay;
    const effectiveDescription =
      newEventAppointmentType === 'meeting'
        ? (newEventMeetingDescription || newEventDescription)
        : newEventAppointmentType === 'time-entry'
          ? (newEventTimeEntryNotes || newEventDescription)
          : newEventDescription;
    if (selectedEvent) {
      if (scope === 'single') {
        setEvents((prev) =>
          prev.map((e) =>
            e.id === selectedEvent.id
              ? {
                ...e,
                title: effectiveTitle,
                description: effectiveDescription,
                start,
                end,
                type: newEventType as CalendarEvent['type'],
                color,
                client: newEventAppointmentType === 'group' ? (newEventClients.length > 0 ? newEventClients.join(', ') : undefined) : newEventClient ?? undefined,
                clients: newEventAppointmentType === 'group' ? newEventClients : undefined,
                groupType: newEventAppointmentType === 'group' ? newEventGroupType || undefined : undefined,
                clientStatuses: newEventAppointmentType === 'group' && newEventClients.length > 0
                  ? Object.fromEntries(newEventClients.map((c) => [c, newEventClientStatuses[c] ?? 'scheduled']))
                  : undefined,
                provider: effectiveProvider || undefined,
                status: newEventAttendanceStatus as CalendarEvent['status'],
                recurringPattern: newEventRecurring as CalendarEvent['recurringPattern'],
                videoLink: newEventVideoLink || undefined,
                modality: newEventModality as CalendarEvent['modality'],
                siteId: siteIdToSave,
                roomId: roomIdToSave,
                deviceIds: deviceIdsToSave,
              }
              : e
          )
        );
        notifications.show({ title: 'Event Updated', message: `${effectiveTitle} has been updated (this instance)`, color: 'green' });
      } else {
        const deltaMs = start.getTime() - selectedEvent.start.getTime();
        setEvents((prev) =>
          prev.map((e) => {
            const isThisOrFuture =
              e.id === selectedEvent.id ||
              (e.recurringPattern &&
                e.recurringPattern !== 'none' &&
                e.title === selectedEvent.title &&
                e.start.getTime() >= selectedEvent.start.getTime());
            if (!isThisOrFuture) return e;
            return {
              ...e,
              title: effectiveTitle,
              description: effectiveDescription,
              start: e.id === selectedEvent.id ? start : new Date(e.start.getTime() + deltaMs),
              end: e.id === selectedEvent.id ? end : new Date(e.end.getTime() + deltaMs),
              type: newEventType as CalendarEvent['type'],
              color,
              client: newEventAppointmentType === 'group' ? (newEventClients.length > 0 ? newEventClients.join(', ') : undefined) : newEventClient ?? undefined,
              clients: newEventAppointmentType === 'group' ? newEventClients : undefined,
              groupType: newEventAppointmentType === 'group' ? newEventGroupType || undefined : undefined,
              clientStatuses: newEventAppointmentType === 'group' && newEventClients.length > 0
                ? Object.fromEntries(newEventClients.map((c) => [c, newEventClientStatuses[c] ?? 'scheduled']))
                : undefined,
              provider: effectiveProvider || undefined,
              status: newEventAttendanceStatus as CalendarEvent['status'],
              recurringPattern: newEventRecurring as CalendarEvent['recurringPattern'],
              videoLink: newEventVideoLink || undefined,
              modality: newEventModality as CalendarEvent['modality'],
              siteId: siteIdToSave,
              roomId: roomIdToSave,
              deviceIds: deviceIdsToSave,
            };
          })
        );
        notifications.show({ title: 'Events Updated', message: 'All future instances updated', color: 'green' });
      }
    } else {
      setEvents((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          title: effectiveTitle,
          description: effectiveDescription,
          start,
          end,
          type: newEventType as CalendarEvent['type'],
          color,
          client: newEventAppointmentType === 'group' ? (newEventClients.length > 0 ? newEventClients.join(', ') : undefined) : newEventClient ?? undefined,
          clients: newEventAppointmentType === 'group' ? newEventClients : undefined,
          groupType: newEventAppointmentType === 'group' ? newEventGroupType || undefined : undefined,
          clientStatuses: newEventAppointmentType === 'group' && newEventClients.length > 0
            ? Object.fromEntries(newEventClients.map((c) => [c, newEventClientStatuses[c] ?? 'scheduled']))
            : undefined,
          provider: effectiveProvider || undefined,
          status: newEventAttendanceStatus as CalendarEvent['status'],
          recurringPattern: showRecurringOptions ? 'weekly' : (newEventRecurring as CalendarEvent['recurringPattern']),
          videoLink: newEventVideoLink || undefined,
          modality: newEventModality as CalendarEvent['modality'],
          siteId: siteIdToSave,
          roomId: roomIdToSave,
          deviceIds: deviceIdsToSave,
        },
      ]);
      notifications.show({
        title: 'Event Created',
        message: `${effectiveTitle} on ${newEventDate.toLocaleDateString('en-US')}`,
        color: 'green',
      });
    }
    setRecurringActionModalOpened(false);
    setModalOpened(false);
  };

  const handleSaveEvent = (scope?: 'single' | 'all') => {
    const effectiveTitle = getEffectiveTitle();
    appointmentForm.setFieldValue('title', effectiveTitle);
    const validation = appointmentForm.validate();
    if (!newEventDate || !effectiveTitle || validation.hasErrors) {
      const firstError = Object.values(validation.errors).find((value) => typeof value === 'string');
      notifications.show({
        title: 'Missing Information',
        message: firstError ?? 'Please fill in required fields',
        color: 'red',
      });
      return;
    }
    const startParsed = parseTimeLabel(newEventStartTime);
    const endParsed = parseTimeLabel(newEventEndTime);
    if (!startParsed || !endParsed) {
      notifications.show({ title: 'Invalid Time', message: 'Please select valid start and end times', color: 'red' });
      return;
    }
    const start = applyZonedWallClock(newEventDate, startParsed.hour, startParsed.minute, displayTimeZone);
    const end = applyZonedWallClock(newEventDate, endParsed.hour, endParsed.minute, displayTimeZone);
    if (end <= start) {
      notifications.show({ title: 'Invalid Time Range', message: 'End time must be after start time', color: 'red' });
      return;
    }
    if (scope !== undefined) {
      performSaveEvent(scope);
      return;
    }
    if (selectedEvent && selectedEvent.recurringPattern && selectedEvent.recurringPattern !== 'none') {
      setRecurringActionType('edit');
      setRecurringActionModalOpened(true);
      return;
    }
    performSaveEvent('single');
  };

  const performDeleteEvent = async (scope: 'single' | 'all') => {
    if (!selectedEvent) return;

    if (isMedplumAppointmentId(selectedEvent.id)) {
      try {
        await deleteCalendarAppointment(medplum, selectedEvent.id);
        refreshLiveEvents();
      } catch (err) {
        notifications.show({
          title: 'Delete Failed',
          message: `Could not delete appointment: ${(err as Error).message}`,
          color: 'red',
        });
        return;
      }
    }

    if (scope === 'single') {
      setEvents((prev) => prev.filter((e) => e.id !== selectedEvent.id));
      notifications.show({ title: 'Event Deleted', message: `${selectedEvent.title} removed (this instance)`, color: 'red' });
    } else {
      setEvents((prev) =>
        prev.filter(
          (e) =>
            e.id !== selectedEvent.id &&
            !(e.recurringPattern && e.recurringPattern !== 'none' && e.title === selectedEvent.title && e.start >= selectedEvent.start)
        )
      );
      notifications.show({ title: 'Recurring Events Deleted', message: 'All future instances removed', color: 'red' });
    }
    setRecurringActionModalOpened(false);
    setModalOpened(false);
  };

  const handleDeleteEvent = () => {
    if (!selectedEvent) return;
    if (selectedEvent.recurringPattern && selectedEvent.recurringPattern !== 'none') {
      setRecurringActionType('delete');
      setRecurringActionModalOpened(true);
      return;
    }
    performDeleteEvent('single');
  };

  const handleCancelAppointment = async () => {
    if (!selectedEvent || isCalendarEventReadOnly(selectedEvent)) return;

    const markLocalCancelled = () => {
      setEvents((prev) =>
        prev.map((event) => {
          if (event.id !== selectedEvent.id) return event;
          const clientStatuses = event.clientStatuses
            ? Object.fromEntries(
              Object.keys(event.clientStatuses).map((name) => [name, 'clinician-cancelled']),
            )
            : undefined;
          return { ...event, status: 'clinician-cancelled' as const, clientStatuses };
        }),
      );
    };

    if (!isMedplumAppointmentId(selectedEvent.id)) {
      markLocalCancelled();
      setModalOpened(false);
      notifications.show({
        title: 'Appointment cancelled',
        message: `${selectedEvent.title} was clinician-cancelled`,
        color: 'yellow',
      });
      return;
    }

    setCalendarCancelling(true);
    try {
      const before = await medplum.readResource('Appointment', selectedEvent.id);
      const after = await medplum.updateResource(applyClinicianCancelToAppointment(before));
      try {
        await syncProviderAppointmentScheduleChange(medplum, {
          before,
          after,
          calendarStatus: 'clinician-cancelled',
        });
      } catch (syncErr) {
        console.warn('[ProviderCalendar] Appointment schedule sync failed:', syncErr);
      }
      markLocalCancelled();
      refreshLiveEvents();
      setModalOpened(false);
      notifications.show({
        title: 'Appointment cancelled',
        message: `${selectedEvent.title} was clinician-cancelled`,
        color: 'yellow',
      });
    } catch (err) {
      notifications.show({
        title: 'Cancel failed',
        message: `Could not cancel appointment: ${getSaveErrorMessage(err)}`,
        color: 'red',
      });
    } finally {
      setCalendarCancelling(false);
    }
  };

  const formatMonthYear = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: displayTimeZone });
  const formatDayHeader = (d: Date) => {
    const parts = getZonedDateTimeParts(d, displayTimeZone);
    return {
      dayName: d.toLocaleDateString('en-US', { weekday: 'short', timeZone: displayTimeZone }),
      dayNumber: parts.day,
      isToday: isSameZonedDay(d, new Date(), displayTimeZone),
    };
  };

  const handleGridCellClick = (day: Date, hour: number, e?: React.MouseEvent<HTMLDivElement>) => {
    if (dropJustHappenedRef.current) {
      dropJustHappenedRef.current = false;
      return;
    }
    let minute = 0;
    if (e?.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      minute = getSnappedMinuteFromRelativePosition(relativeY, rect.height);
    }
    const parts = getZonedDateTimeParts(day, displayTimeZone);
    openNewEventModal(new Date(parts.year, parts.month - 1, parts.day), hour, minute);
  };

  // ----- Month view -----
  const renderMonthView = () => {
    const currentMonth = getZonedDateTimeParts(currentDate, displayTimeZone).month;
    const weeks: Date[][] = [];
    for (let i = 0; i < monthDays.length; i += 7) weeks.push(monthDays.slice(i, i + 7));

    return (
      <Paper
        withBorder
        style={{
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          borderWidth: 1,
          borderColor: BORDER_COLOR,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            borderBottom: `1px solid ${BORDER_COLOR}`,
            backgroundColor: 'var(--unify-bg-paper)',
          }}
        >
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} style={{ padding: 12, textAlign: 'center', borderRight: '1px solid var(--mantine-color-gray-3)' }}>
              <Text size="sm" fw={600} c="gray.7">
                {day}
              </Text>
            </div>
          ))}
        </div>
        {weeks.map((week, weekIndex) => {
          const rowExpanded = expandedRows.has(weekIndex);
          return (
            <div key={weekIndex} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {week.map((day, dayIndex) => {
                const dayEvents = getEventsForDay(day);
                const dayParts = getZonedDateTimeParts(day, displayTimeZone);
                const isCurrentMonth = dayParts.month === currentMonth;
                const isToday = isSameZonedDay(day, new Date(), displayTimeZone);
                const displayLimit = rowExpanded ? dayEvents.length : 3;
                return (
                  <div
                    key={dayIndex}
                    style={{
                      minHeight: 120,
                      padding: 8,
                      borderRight: '1px solid var(--mantine-color-gray-3)',
                      borderBottom: '1px solid var(--mantine-color-gray-3)',
                      backgroundColor: isCurrentMonth ? 'var(--unify-bg-paper)' : 'var(--unify-grey-100)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    onClick={() =>
                      openNewEventModal(new Date(dayParts.year, dayParts.month - 1, dayParts.day))
                    }
                  >
                    <div
                      style={{
                        display: 'inline-flex',
                        width: 28,
                        height: 28,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: isToday ? TODAY_BG : 'transparent',
                        marginBottom: 4,
                      }}
                    >
                      <Text size="sm" fw={isToday ? 600 : 400} c={isToday ? 'white' : isCurrentMonth ? 'gray.9' : 'gray.5'}>
                        {dayParts.day}
                      </Text>
                    </div>
                    <Stack gap={2} style={{ flex: 1 }}>
                      {dayEvents.slice(0, displayLimit).map((event) => {
                        const scheme = eventDisplayColor(event);
                        return (
                          <div
                            key={event.id}
                            style={{
                              padding: '4px 6px',
                              backgroundColor: scheme.bg,
                              border: `1px solid ${scheme.border}`,
                              borderRadius: 4,
                              cursor: 'pointer',
                              width: '100%',
                              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditEventModal(event);
                            }}
                          >
                            <Text size="xs" lineClamp={1} fw={500} c={scheme.text} style={scheme.strikethrough ? { textDecoration: 'line-through' } : undefined}>
                              {event.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: displayTimeZone })}: {getEventTypeLabel(event)}
                            </Text>
                          </div>
                        );
                      })}
                      {dayEvents.length > displayLimit && !rowExpanded && (
                        <Text
                          size="xs"
                          c="blue.6"
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedRows((prev) => new Set([...prev, weekIndex]));
                          }}
                        >
                          +{dayEvents.length - displayLimit} more
                        </Text>
                      )}
                      {rowExpanded && dayEvents.length > 3 && (
                        <Text
                          size="xs"
                          c="blue.6"
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedRows((prev) => {
                              const next = new Set(prev);
                              next.delete(weekIndex);
                              return next;
                            });
                          }}
                        >
                          Show less
                        </Text>
                      )}
                    </Stack>
                  </div>
                );
              })}
            </div>
          );
        })}
      </Paper>
    );
  };

  // ----- Week view -----
  const renderWeekView = () => (
    <Paper
      withBorder
      style={{
        overflow: 'auto',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px',
        borderWidth: 1,
        borderColor: BORDER_COLOR,
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <div style={{ minWidth: 900 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '60px repeat(7, 1fr)',
            borderBottom: `1px solid ${BORDER_COLOR}`,
            position: 'sticky',
            top: 0,
            backgroundColor: 'var(--unify-bg-paper)',
            zIndex: 10,
          }}
        >
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
            <Text size="xs" c="gray.6">
              {getTimeZoneShort(displayTimeZone)}
            </Text>
          </div>
          {weekDays.map((day, idx) => {
            const { dayName, dayNumber, isToday } = formatDayHeader(day);
            return (
              <div key={idx} style={{ padding: 12, textAlign: 'center', borderLeft: '1px solid var(--mantine-color-gray-3)' }}>
                <Text size="xs" c="gray.6" fw={600} tt="uppercase">
                  {dayName}
                </Text>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    margin: '4px auto 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isToday ? TODAY_BG : 'transparent',
                    color: isToday ? 'white' : 'inherit',
                  }}
                >
                  <Text size="sm" fw={isToday ? 600 : 400}>
                    {dayNumber}
                  </Text>
                </div>
              </div>
            );
          })}
        </div>
        <TimeGridDropZone
          weekDays={weekDays}
          onEventMove={handleEventMove}
          timeZone={displayTimeZone}
          onDropConsumed={() => {
            dropJustHappenedRef.current = true;
          }}
        >
          {HOURS.map((hour) => (
            <div
              key={hour}
              style={{
                display: 'grid',
                gridTemplateColumns: '60px repeat(7, 1fr)',
                borderBottom: '1px solid var(--mantine-color-gray-3)',
                height: PIXELS_PER_HOUR,
                minHeight: PIXELS_PER_HOUR,
              }}
            >
              <div style={{ padding: '4px 8px', fontSize: 12, color: 'var(--mantine-color-gray-6)', textAlign: 'right' }}>
                {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </div>
              {weekDays.map((day, dayIndex) => (
                <div
                  key={dayIndex}
                  style={{
                    position: 'relative',
                    borderLeft: '1px solid var(--mantine-color-gray-3)',
                    cursor: 'pointer',
                  }}
                >
                  {getUnavailableRangesInHour(day, hour).map((range, ri) => (
                    <div
                      key={ri}
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: `${(range.startMinute / 60) * 100}%`,
                        height: `${((range.endMinute - range.startMinute) / 60) * 100}%`,
                        backgroundColor: 'var(--mantine-color-gray-1)',
                        pointerEvents: 'none',
                      }}
                    />
                  ))}
                  <div
                    style={{ height: '100%' }}
                    onClick={(e) => handleGridCellClick(day, hour, e)}
                  />
                </div>
              ))}
            </div>
          ))}
          {/* Current time line */}
          {(() => {
            const now = new Date();
            const nowParts = getZonedDateTimeParts(now, displayTimeZone);
            const currentDayIndex = weekDays.findIndex((d) => isSameZonedDay(d, now, displayTimeZone));
            if (currentDayIndex === -1 || nowParts.hour < 7 || nowParts.hour >= 20) return null;
            const top = getCurrentTimePosition();
            const colWidth = '(100% - 60px) / 7';
            return (
              <div
                style={{
                  position: 'absolute',
                  left: `calc(60px + ${colWidth} * ${currentDayIndex})`,
                  width: `calc(${colWidth})`,
                  top,
                  height: 2,
                  backgroundColor: TODAY_BG,
                  zIndex: 10,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: -4,
                    top: -4,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: TODAY_BG,
                  }}
                />
              </div>
            );
          })()}
          {/* Events */}
          {weekDays.map((day, dayIndex) => {
            const dayEvents = getEventsForDay(day);
            const layoutMap = getEventLayout(dayEvents);
            return dayEvents.map((event) => {
              const { top, height } = getEventPosition(event);
              const layout = layoutMap.get(event.id) ?? { column: 0, totalColumns: 1 };
              const colWidth = '(100% - 60px) / 7';
              const slotWidth = `(${colWidth}) / ${layout.totalColumns}`;
              const eventWidth = `(${slotWidth}) * 0.92`;
              const scheme = eventDisplayColor(event);
              const strikeStyle = scheme.strikethrough ? { textDecoration: 'line-through' as const } : undefined;
              return (
                <div
                  key={event.id}
                  style={{
                    position: 'absolute',
                    left: `calc(60px + ${colWidth} * ${dayIndex} + ${slotWidth} * ${layout.column})`,
                    width: `calc(${eventWidth})`,
                    top,
                    height,
                    zIndex: 5,
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                >
                  <DraggableEvent event={event} onEdit={openEditEventModal}>
                    <Card
                      shadow="xs"
                      padding={0}
                      withBorder
                      style={{
                        height: '100%',
                        position: 'relative',
                        backgroundColor: scheme.bg,
                        borderColor: scheme.border,
                        borderStyle: 'solid',
                        borderRadius: 4,
                        overflow: 'hidden',
                        padding: height <= 45 ? '2px 8px' : '8px 10px',
                      }}
                    >
                      <div style={{ position: 'absolute', top: height <= 45 ? 2 : 4, right: 4, lineHeight: 0, display: 'flex', gap: 2 }}>
                        {event.isInvitedEvent && (
                          <Tooltip label="Invited — view only" withArrow position="top">
                            <IconLock size={12} color="var(--mantine-color-gray-6)" />
                          </Tooltip>
                        )}
                        {event.modality && event.type !== 'blocked' && (
                          <Tooltip label={event.modality === 'telehealth' ? 'Telehealth' : event.modality === 'hybrid' ? 'Hybrid' : 'In-person'} withArrow position="top">
                            <span style={{ lineHeight: 0, display: 'flex', gap: 2 }}>
                              {event.modality === 'telehealth' ? (
                                <IconDeviceLaptop size={14} color="var(--mantine-color-gray-6)" />
                              ) : event.modality === 'hybrid' ? (
                                <>
                                  <IconBuilding size={12} color="var(--mantine-color-gray-6)" />
                                  <IconDeviceLaptop size={12} color="var(--mantine-color-gray-6)" />
                                </>
                              ) : (
                                <IconBuilding size={14} color="var(--mantine-color-gray-6)" />
                              )}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <Text
                        size="xs"
                        fw={500}
                        lineClamp={1}
                        c={scheme.text}
                        style={{
                          paddingRight:
                            (event.modality && event.type !== 'blocked') ||
                            isCalendarEventReadOnly(event)
                              ? event.modality === 'hybrid'
                                ? 42
                                : 28
                              : 0,
                          ...strikeStyle,
                        }}
                        aria-label={calendarEventAriaLabel(event)}
                      >
                        {multiColorMode && event.provider
                          ? `${getEventTypeLabel(event)} · ${event.provider}`
                          : getEventTypeLabel(event)}
                      </Text>
                      {height > 30 && (
                        <Text size="xs" c="gray.7" mt={2} style={strikeStyle}>
                          {event.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: displayTimeZone })}
                        </Text>
                      )}
                      {height > 60 && event.client && (
                        <Text size="xs" c="gray.6" lineClamp={1} mt={2} style={strikeStyle}>
                          {event.client}
                        </Text>
                      )}
                      {event.type === 'blocked' && event.description && (
                        <Text size="xs" c="gray.6" lineClamp={1} mt={2}>
                          {event.description}
                        </Text>
                      )}
                    </Card>
                  </DraggableEvent>
                </div>
              );
            });
          })}
        </TimeGridDropZone>
      </div>
    </Paper>
  );

  // ----- Day view -----
  const renderDayView = () => (
    <Paper
      withBorder
      style={{
        overflow: 'auto',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px',
        borderWidth: 1,
        borderColor: BORDER_COLOR,
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <div style={{ minWidth: 600 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr',
            borderBottom: `1px solid ${BORDER_COLOR}`,
            position: 'sticky',
            top: 0,
            backgroundColor: 'var(--unify-bg-paper)',
            zIndex: 10,
          }}
        >
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
            <Text size="xs" c="gray.6">
              {getTimeZoneShort(displayTimeZone)}
            </Text>
          </div>
          <div style={{ padding: 12, textAlign: 'center', borderLeft: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" fw={600} c="gray.7">
              {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: displayTimeZone })}
            </Text>
          </div>
        </div>
        <TimeGridDropZone
          weekDays={[currentDate]}
          onEventMove={handleEventMove}
          timeZone={displayTimeZone}
          onDropConsumed={() => {
            dropJustHappenedRef.current = true;
          }}
        >
          {HOURS.map((hour) => (
            <div
              key={hour}
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr',
                borderBottom: '1px solid var(--mantine-color-gray-3)',
                height: PIXELS_PER_HOUR,
                minHeight: PIXELS_PER_HOUR,
              }}
            >
              <div style={{ padding: '4px 8px', fontSize: 12, color: 'var(--mantine-color-gray-6)', textAlign: 'right' }}>
                {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </div>
              <div
                style={{
                  position: 'relative',
                  borderLeft: '1px solid var(--mantine-color-gray-3)',
                  cursor: 'pointer',
                }}
              >
                {getUnavailableRangesInHour(currentDate, hour).map((range, ri) => (
                  <div
                    key={ri}
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: `${(range.startMinute / 60) * 100}%`,
                      height: `${((range.endMinute - range.startMinute) / 60) * 100}%`,
                      backgroundColor: 'var(--mantine-color-gray-1)',
                      pointerEvents: 'none',
                    }}
                  />
                ))}
                <div
                  style={{ height: '100%' }}
                  onClick={(e) => handleGridCellClick(currentDate, hour, e)}
                />
              </div>
            </div>
          ))}
          {(() => {
            const now = new Date();
            const nowParts = getZonedDateTimeParts(now, displayTimeZone);
            const isToday = isSameZonedDay(currentDate, now, displayTimeZone);
            if (!isToday || nowParts.hour < 7 || nowParts.hour >= 20) return null;
            const top = getCurrentTimePosition();
            return (
              <div
                style={{
                  position: 'absolute',
                  left: 60,
                  width: 'calc(100% - 60px)',
                  top,
                  height: 2,
                  backgroundColor: TODAY_BG,
                  zIndex: 10,
                  pointerEvents: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: -4,
                    top: -4,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: TODAY_BG,
                  }}
                />
              </div>
            );
          })()}
          {(() => {
            const dayEvents = getEventsForDay(currentDate);
            const layoutMap = getEventLayout(dayEvents);
            return dayEvents.map((event) => {
              const { top, height } = getEventPosition(event);
              const layout = layoutMap.get(event.id) ?? { column: 0, totalColumns: 1 };
              const totalWidth = 'calc(100% - 60px)';
              const slotWidth = `(${totalWidth}) / ${layout.totalColumns}`;
              const eventWidth = `(${slotWidth}) * 0.92`;
              const scheme = eventDisplayColor(event);
              const strikeStyle = scheme.strikethrough ? { textDecoration: 'line-through' as const } : undefined;
              return (
                <div
                  key={event.id}
                  style={{
                    position: 'absolute',
                    left: `calc(60px + ${slotWidth} * ${layout.column})`,
                    width: `calc(${eventWidth})`,
                    top,
                    height,
                    zIndex: 5,
                    paddingLeft: 2,
                    paddingRight: 2,
                  }}
                >
                  <DraggableEvent event={event} onEdit={openEditEventModal}>
                    <Card
                      shadow="xs"
                      padding={0}
                      withBorder
                      style={{
                        height: '100%',
                        position: 'relative',
                        backgroundColor: scheme.bg,
                        borderColor: scheme.border,
                        borderStyle: 'solid',
                        borderRadius: 4,
                        overflow: 'hidden',
                        padding: height <= 45 ? '2px 8px' : '8px 10px',
                      }}
                    >
                      <div style={{ position: 'absolute', top: height <= 45 ? 2 : 4, right: 4, lineHeight: 0, display: 'flex', gap: 2 }}>
                        {event.isInvitedEvent && (
                          <Tooltip label="Invited — view only" withArrow position="top">
                            <IconLock size={12} color="var(--mantine-color-gray-6)" />
                          </Tooltip>
                        )}
                        {event.modality && event.type !== 'blocked' && (
                          <Tooltip label={event.modality === 'telehealth' ? 'Telehealth' : event.modality === 'hybrid' ? 'Hybrid' : 'In-person'} withArrow position="top">
                            <span style={{ lineHeight: 0, display: 'flex', gap: 2 }}>
                              {event.modality === 'telehealth' ? (
                                <IconDeviceLaptop size={14} color="var(--mantine-color-gray-6)" />
                              ) : event.modality === 'hybrid' ? (
                                <>
                                  <IconBuilding size={12} color="var(--mantine-color-gray-6)" />
                                  <IconDeviceLaptop size={12} color="var(--mantine-color-gray-6)" />
                                </>
                              ) : (
                                <IconBuilding size={14} color="var(--mantine-color-gray-6)" />
                              )}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <Text
                        size="xs"
                        fw={500}
                        lineClamp={1}
                        c={scheme.text}
                        style={{
                          paddingRight:
                            (event.modality && event.type !== 'blocked') ||
                            isCalendarEventReadOnly(event)
                              ? event.modality === 'hybrid'
                                ? 42
                                : 28
                              : 0,
                          ...strikeStyle,
                        }}
                        aria-label={calendarEventAriaLabel(event)}
                      >
                        {multiColorMode && event.provider
                          ? `${getEventTypeLabel(event)} · ${event.provider}`
                          : getEventTypeLabel(event)}
                      </Text>
                      {height > 30 && (
                        <Text size="xs" c="gray.7" mt={2} style={strikeStyle}>
                          {event.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: displayTimeZone })}
                        </Text>
                      )}
                      {height > 60 && event.client && (
                        <Text size="xs" c="gray.6" lineClamp={1} mt={2} style={strikeStyle}>
                          {event.client}
                        </Text>
                      )}
                      {event.type === 'blocked' && event.description && (
                        <Text size="xs" c="gray.6" lineClamp={1} mt={2}>
                          {event.description}
                        </Text>
                      )}
                    </Card>
                  </DraggableEvent>
                </div>
              );
            });
          })()}
        </TimeGridDropZone>
      </div>
    </Paper>
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <Container size="xl" py="xl">
        <Stack gap={0}>
          <Group justify="space-between" align="flex-end" mb="lg">
            <Box>
              <Title order={2}>Provider Calendar</Title>
              <Text c="gray.6" size="sm" mt="xs">
                Manage appointments, availability, and time tracking
              </Text>
            </Box>
            <Button color="gold" leftSection={<IconPlus size={16} />} onClick={() => openNewEventModal()}>
              New Event
            </Button>
          </Group>

          {liveError && (
            <Alert
              color="red"
              variant="light"
              title="Couldn't load your calendar"
              mb="md"
              withCloseButton={false}
              styles={{ root: { borderColor: 'var(--mantine-color-red-3)' } }}
            >
              <Group justify="space-between" align="center" wrap="nowrap">
                <Text size="sm">{liveError}</Text>
                <Button size="xs" variant="light" color="red" onClick={() => refreshLiveEvents()}>
                  Retry
                </Button>
              </Group>
            </Alert>
          )}

          {!hasResolvedPractitioner && !liveError && (
            <Alert color="yellow" variant="light" title="Provider profile not linked" mb="md">
              <Text size="sm">
                We couldn&apos;t resolve your clinician profile in Medplum, so this calendar is empty.
                Ask an admin to link your user to a Practitioner resource.
              </Text>
            </Alert>
          )}

          <Paper
            p="md"
            withBorder
            style={{
              borderBottom: 'none',
              borderRadius: '8px 8px 0 0',
              borderWidth: 1,
              borderColor: BORDER_COLOR,
            }}
          >
            <Group justify="space-between" align="center">
              <Group gap="md">
                <Title order={3}>
                  {formatMonthYear(currentDate)}
                </Title>
                <Group gap="xs">
                  <Tooltip label={`Previous ${view}`}>
                    <Button variant="outline" color="gray" size="sm" p={0} w={36} h={36} onClick={() => navigateCalendar('prev')} aria-label={`Previous ${view}`}>
                      <IconChevronLeft size={18} />
                    </Button>
                  </Tooltip>
                  <Tooltip label={`Next ${view}`}>
                    <Button variant="outline" color="gray" size="sm" p={0} w={36} h={36} onClick={() => navigateCalendar('next')} aria-label={`Next ${view}`}>
                      <IconChevronRight size={18} />
                    </Button>
                  </Tooltip>
                </Group>
                <Button variant="outline" color="gray" size="sm" onClick={() => setCurrentDate(new Date())}>
                  Today
                </Button>
                <Popover position="bottom-start" withArrow shadow="md">
                  <Popover.Target>
                    <Tooltip label="Select date">
                      <ActionIcon variant="outline" color="gray" size={36} style={{ borderColor: BORDER_COLOR }} aria-label="Select date">
                        <IconCalendarEvent size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <DatePicker
                      value={(() => {
                        const parts = getZonedDateTimeParts(currentDate, displayTimeZone);
                        return new Date(parts.year, parts.month - 1, parts.day);
                      })()}
                      onChange={(date) => {
                        const parsed = calendarDateFromPickerValue(date);
                        if (!parsed) return;
                        setCurrentDate(
                          zonedWallClockToUtcDate(
                            parsed.getFullYear(),
                            parsed.getMonth() + 1,
                            parsed.getDate(),
                            12,
                            0,
                            displayTimeZone,
                          ),
                        );
                      }}
                      size="sm"
                    />
                  </Popover.Dropdown>
                </Popover>
              </Group>
              <Group gap="sm">
                {showPractitionerSelector && (
                  <Box w={300}>
                    <FilterMultiSelect
                      allLabel="All accessible calendars"
                      data={practitionerSelectorData}
                      value={selectedPractitionerIds}
                      onChange={handleSelectedPractitionerIdsChange}
                      emptyMeansAll={false}
                      hideAllOption={false}
                      placeholder="Select calendars"
                      searchPlaceholder="Search practitioners"
                      w={300}
                      size="sm"
                    />
                  </Box>
                )}
                <FilterMultiSelect
                  allLabel="All episode types"
                  data={eocTypeFilterOptions}
                  value={eocTypeFilter}
                  onChange={setEocTypeFilter}
                  w={280}
                  size="sm"
                  disabled={eocTypeFilterOptions.length === 0}
                />
                <Tooltip label="Filters">
                  <Button variant="outline" color="gray" size="sm" p={0} w={36} h={36} onClick={() => setFilterDrawerOpened(true)} aria-label="Filters">
                    <IconFilter size={18} />
                  </Button>
                </Tooltip>
                <Menu position="bottom-end" withArrow shadow="md">
                  <Menu.Target>
                    <Tooltip label="Calendar settings">
                      <Button variant="outline" color="gray" size="sm" p={0} w={36} h={36} aria-label="Calendar settings">
                        <IconSettings size={18} />
                      </Button>
                    </Tooltip>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconCalendarEvent size={16} />}
                      onClick={() => {
                        setCalendarViewDraft(view);
                        setCalendarViewModalOpen(true);
                      }}
                    >
                      Calendar view ({CALENDAR_VIEW_OPTIONS.find((option) => option.value === view)?.label ?? 'Weekly'})
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconCalendarClock size={16} />}
                      disabled={!livePractitionerId || availabilityLoading}
                      onClick={() => {
                        if (!livePractitionerId) return;
                        setAvailabilityModalOpen(true);
                        setAvailabilityLoading(true);
                        void (async () => {
                          try {
                            const { weekly } = await loadProviderWeeklyAvailability(
                              medplum,
                              livePractitionerId,
                            );
                            setAvailabilityDraft(
                              mergeWeeklyAvailability(JSON.parse(JSON.stringify(weekly))),
                            );
                          } catch {
                            notifications.show({
                              title: 'Could not load availability',
                              message: 'Using default hours. Try again or save to create your schedule.',
                              color: 'red',
                            });
                            setAvailabilityDraft(
                              mergeWeeklyAvailability(JSON.parse(JSON.stringify(weeklyAvailability))),
                            );
                          } finally {
                            setAvailabilityLoading(false);
                          }
                        })();
                      }}
                    >
                      Manage Availability
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconDoor size={16} />}
                      onClick={() => setRoomBrowseDrawerOpen(true)}
                    >
                      Room Availability
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSettings size={16} />}
                      onClick={() => navigate('/dashboard/settings/calendar')}
                    >
                      Settings
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
          </Paper>

          {view === 'month' && renderMonthView()}
          {view === 'week' && renderWeekView()}
          {view === 'day' && renderDayView()}
        </Stack>

        <Modal
          opened={calendarViewModalOpen}
          onClose={() => setCalendarViewModalOpen(false)}
          title="Calendar view"
          size="sm"
          centered
          styles={{
            header: { borderBottom: `1px solid ${BORDER_COLOR}`, paddingBottom: 12 },
            body: { paddingTop: 16 },
            content: { border: `1px solid ${BORDER_COLOR}` },
          }}
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Choose how appointments are laid out on the calendar.
            </Text>
            <Radio.Group
              value={calendarViewDraft}
              onChange={(value) => setCalendarViewDraft(value as CalendarView)}
            >
              <Stack gap="sm">
                {CALENDAR_VIEW_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value} label={option.label} />
                ))}
              </Stack>
            </Radio.Group>
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" color="gray" onClick={() => setCalendarViewModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setView(calendarViewDraft);
                  setCalendarViewModalOpen(false);
                }}
              >
                Apply
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Drawer
          opened={filterDrawerOpened}
          onClose={() => setFilterDrawerOpened(false)}
          title="Filters"
          position="right"
          size="sm"
        >
          <Stack gap="md">
            <Text size="sm" fw={600}>Event type</Text>
            <Checkbox.Group value={eventTypeFilter} onChange={(v) => setEventTypeFilter(v)}>
              <Stack gap="xs">
                <Checkbox value="appointment" label="Appointment" />
                <Checkbox value="meeting" label="Meeting" />
                <Checkbox value="task" label="Task" />
                <Checkbox value="blocked" label="Blocked time" />
              </Stack>
            </Checkbox.Group>
            <Divider />
            <Text size="sm" fw={600}>Status</Text>
            <Checkbox.Group value={statusFilter} onChange={(v) => setStatusFilter(v)}>
              <Stack gap="xs">
                <Checkbox value="scheduled" label="Scheduled" />
                <Checkbox value="proposed" label="Proposed" />
                <Checkbox value="attended" label="Attended" />
                <Checkbox value="clinician-cancelled" label="Clinician cancelled" />
                <Checkbox value="client-cancelled" label="Client cancelled" />
                <Checkbox value="client-late-cancelled" label="Late cancelled" />
                <Checkbox value="client-no-show" label="No show" />
              </Stack>
            </Checkbox.Group>
            <Divider />
            <Group justify="space-between">
              <Button variant="subtle" color="gray" size="sm" onClick={() => { setEventTypeFilter(['appointment', 'meeting', 'task', 'blocked']); setStatusFilter([]); setEocTypeFilter([]); }}>
                Clear filters
              </Button>
              <Button variant="outline" color="gray" onClick={() => setFilterDrawerOpened(false)}>
                Apply filters
              </Button>
            </Group>
          </Stack>
        </Drawer>

        <Modal
          opened={availabilityModalOpen}
          onClose={() => {
            if (!availabilitySaving) setAvailabilityModalOpen(false);
          }}
          title="Manage Availability"
          size="md"
          centered
          styles={{
            header: { borderBottom: `1px solid ${BORDER_COLOR}`, paddingBottom: 12 },
            body: { paddingTop: 16 },
            content: { border: `1px solid ${BORDER_COLOR}` },
          }}
        >
          <Stack gap="md">
            <WeeklyAvailabilityEditor
              value={availabilityDraft}
              onChange={setAvailabilityDraft}
              loading={availabilityLoading}
            />
            <Group justify="flex-end" gap="sm" mt="md">
              <Button variant="subtle" color="gray" onClick={() => setAvailabilityModalOpen(false)}>
                Cancel
              </Button>
              <Tooltip label={getProviderWeeklyAvailabilityUiDisableReason(availabilityDraft)}>
                <span>
                  <Button
                    color="gold"
                    loading={availabilitySaving}
                    disabled={
                      !livePractitionerId ||
                      availabilityLoading ||
                      !parseProviderWeeklyAvailabilityUi(availabilityDraft).success
                    }
                    onClick={() => {
                      if (!livePractitionerId) return;
                      const parsed = parseProviderWeeklyAvailabilityUi(availabilityDraft);
                      if (!parsed.success) return;
                      setAvailabilitySaving(true);
                      void (async () => {
                        try {
                          const practitionerDisplay =
                            profile?.resourceType === 'Practitioner'
                              ? formatPractitionerPreferredLabel(profile)
                              : undefined;
                          await saveProviderWeeklyAvailability(
                            medplum,
                            livePractitionerId,
                            availabilityDraft,
                            { practitionerDisplay: practitionerDisplay || undefined },
                          );
                          setWeeklyAvailability(mergeWeeklyAvailability(availabilityDraft));
                          setAvailabilityModalOpen(false);
                          notifications.show({
                            title: 'Availability saved',
                            message: 'Your weekly schedule is updated for client booking.',
                            color: 'green',
                          });
                        } catch {
                          notifications.show({
                            title: 'Could not save availability',
                            message: 'Please try again.',
                            color: 'red',
                          });
                        } finally {
                          setAvailabilitySaving(false);
                        }
                      })();
                    }}
                  >
                    Save
                  </Button>
                </span>
              </Tooltip>
            </Group>
          </Stack>
        </Modal>

        <AppointmentEditorModal
          source="calendar"
          opened={modalOpened}
          onClose={() => setModalOpened(false)}
          appointment={editorAppointment}
          organizationId={orgId ?? ''}
          viewerPractitionerId={practitionerId || livePractitionerId || ''}
          title={calendarModalTitle}
          groupDisplayName={
            newEventAppointmentType === 'group'
              ? (therapyGroupSelectData.find((o) => o.value === newEventTherapyGroupId)?.label
                ?? selectedEvent?.therapyGroupDisplay)
              : undefined
          }
          episodeDisplayName={
            selectedEpisodeDetail?.episodeDisplayName
            ?? selectedEpisodeEocType?.name
            ?? selectedEvent?.sessionTypeDisplay
          }
          patientDisplay={
            selectedEpisodeDetail?.clientName ?? newEventClient ?? selectedEvent?.client
          }
          patientId={selectedEpisodeDetail?.patientId ?? selectedEvent?.patientId}
          episodeOfCareId={newEventEpisodeOfCare || selectedEvent?.episodeOfCareId || undefined}
          participantRefs={newEventSessionAttendees}
          participantOptions={sessionAttendeeOptions}
          onParticipantRefsChange={(participantRefs) =>
            applySessionFieldPatch({ sessionAttendees: participantRefs })
          }
          category={newEventAppointmentType}
          onCategoryChange={(next) => {
            setNewEventAppointmentType(next);
            setNewEventType(
              next === 'time-entry' ? 'blocked' : next === 'meeting' ? 'meeting' : 'appointment',
            );
          }}
          readOnly={Boolean(selectedEvent && isCalendarEventReadOnly(selectedEvent))}
          videoLink={newEventVideoLink || selectedEvent?.videoLink}
          showJoinVideo={
            Boolean(selectedEvent)
            && shouldShowCalendarJoin({
              isTelehealthOrHybrid:
                newEventModality === 'telehealth' || newEventModality === 'hybrid',
              isParticipant: Boolean(
                selectedEvent
                && viewerIsCalendarEventParticipant(selectedEvent, {
                  practitionerId: accessSelfId || practitionerId || undefined,
                }),
              ),
              hasVideoMeeting: Boolean((newEventVideoLink || selectedEvent?.videoLink)?.trim()),
            })
          }
          onJoinVideo={
            selectedEvent
              ? () => {
                const sessionMockState = buildMockAppointmentPageStateFromCalendarEvent({
                  id: selectedEvent.id,
                  title: selectedEvent.title,
                  start: selectedEvent.start,
                  end: selectedEvent.end,
                  client: selectedEvent.client,
                  provider: selectedEvent.provider,
                  status: selectedEvent.status,
                });
                navigate(`/dashboard/session/${selectedEvent.id}`, {
                  state: { mockAppointmentData: sessionMockState },
                });
              }
              : undefined
          }
          onDelete={selectedEvent ? handleDeleteEvent : undefined}
          onCancelAppointment={
            selectedEvent && !isCalendarEventReadOnly(selectedEvent)
              ? () => void handleCancelAppointment()
              : undefined
          }
          calendarCancelling={calendarCancelling}
          editing={Boolean(selectedEvent)}
          onCalendarSave={() => handleSaveEvent()}
          calendarSaveDisabled={
            newEventAppointmentType === 'session' && !selectedEvent && !newEventEpisodeOfCare
          }
          onSaved={() => {
            refreshLiveEvents();
          }}
        >
          <>
              {newEventAppointmentType === 'session' && (
                <SessionIdentityFields
                  values={appointmentForm.values}
                  onChange={applySessionFieldPatch}
                  errors={appointmentForm.errors}
                  editing={Boolean(selectedEvent)}
                  episodeOptions={episodeOfCareOptions}
                  episodesLoading={calendarFormOptions.episodesLoading}
                  episodeDetails={calendarFormOptions.liveEpisodeDetails}
                  providerOptions={sessionProviderSelectData}
                  providersLoading={calendarFormOptions.loading}
                  sessionTypeOptions={sessionTypeSelectData}
                  attendeeOptions={sessionAttendeeOptions}
                  hideAttendees
                  statusOptions={topLevelEventStatusSelectData}
                  sessionTypeDurations={sessionTypeDurations}
                  startTime={newEventStartTime}
                  onAttendanceStatusChange={handleAttendanceStatusChange}
                />
              )}

              {newEventAppointmentType === 'group' && (
                <>
                  <Select
                    label="Group cohort"
                    placeholder={therapyGroupsLoading ? 'Loading groups…' : 'Select group'}
                    value={newEventTherapyGroupId || null}
                    onChange={(v) => {
                      const id = v ?? '';
                      setNewEventTherapyGroupId(id);
                      applyTherapyGroupPrefill(therapyGroups.find((g) => g.id === id));
                    }}
                    withAsterisk
                    data={therapyGroupSelectData}
                    searchable
                    disabled={therapyGroupsLoading || !!selectedEvent}
                    nothingFoundMessage="No active groups"
                    error={appointmentForm.errors.therapyGroupId}
                  />
                  <Select
                    label="Session type"
                    description="Defaults to the cohort's session service; drives billing and the note template."
                    placeholder="Select session type"
                    value={newEventSessionType || null}
                    onChange={(v) => setNewEventSessionType(v ?? '')}
                    data={groupSessionTypeSelectData}
                    searchable
                  />
                  <div>
                    <Select
                      label={showOtherCoFacilitators ? 'Primary facilitator' : 'Facilitator'}
                      placeholder="Select facilitator"
                      value={newEventProvider || null}
                      onChange={(v) => {
                        const id = v ?? '';
                        setNewEventProvider(id);
                        setNewEventFacilitator(
                          calendarFormOptions.practitionerDisplayById.get(id) ?? id,
                        );
                      }}
                      withAsterisk
                      leftSection={<IconUser size={16} />}
                      data={allProviderSelectData}
                      searchable
                      error={appointmentForm.errors.facilitator || appointmentForm.errors.provider}
                    />
                    {!showOtherCoFacilitators ? (
                      <Text size="sm" c="blue.6" mt={4} style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowOtherCoFacilitators(true)}>
                        Add co-facilitator...
                      </Text>
                    ) : (
                      <>
                        <MultiSelect
                          label="Co-facilitator(s)"
                          placeholder="Select co-facilitators"
                          value={newEventCoFacilitators}
                          onChange={setNewEventCoFacilitators}
                          leftSection={<IconUsers size={16} />}
                          data={allProviderSelectData}
                          clearable
                          mt="md"
                        />
                        <Text size="sm" c="blue.6" mt={4} style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { setShowOtherCoFacilitators(false); setNewEventCoFacilitators([]); }}>
                          Remove co-facilitators
                        </Text>
                      </>
                    )}
                  </div>
                  {selectedEvent ? (
                    <div>
                      <Text fw={500} size="sm">Group status <Text component="span" c="red" size="sm">*</Text></Text>
                      <Text size="xs" c="dimmed" mb={4}>Top-level group status (individual participant attendance tracked below)</Text>
                      <Select
                        value={newEventAttendanceStatus}
                        onChange={handleAttendanceStatusChange}
                        data={topLevelEventStatusSelectData}
                        leftSection={<Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: CLIENT_STATUS_DOT_COLORS[newEventAttendanceStatus] ?? 'var(--mantine-color-gray-6)' }} />}
                      />
                    </div>
                  ) : (
                    <div>
                      <MultiSelect
                        label="Clients"
                        placeholder="Select clients"
                        value={newEventClients}
                        onChange={setNewEventClients}
                        leftSection={<IconUsers size={16} />}
                        data={[
                          ...clientSelectData,
                          ...newEventClients
                            .filter((c) => !clientSelectData.some((d) => d.value === c))
                            .map((c) => ({ value: c, label: c })),
                        ]}
                        renderOption={({ option }) => (
                          <ClientSelectOptionLabel label={option.label} />
                        )}
                        clearable
                        searchable
                        withAsterisk
                        error={appointmentForm.errors.clients}
                      />
                    </div>
                  )}
                  <GroupAppointmentScheduleFields
                    value={{
                      date: newEventDate,
                      startTime: newEventStartTime,
                      endTime: newEventEndTime,
                      recurringEnabled: showRecurringOptions,
                      scheduleMode: showRecurringOptions ? 'recurring' : 'custom',
                      sessionDates: [],
                      recurringInterval,
                      recurringTimeframe: (recurringTimeframe === 'month' ? 'month' : 'week'),
                      recurringDaysOfWeek,
                      recurringEndType,
                      recurringEndDate,
                      recurringOccurrences,
                      modality: newEventModality as GroupAppointmentScheduleFieldsValue['modality'],
                      locationId: newEventLocation,
                      telehealthFromOffice: newEventTelehealthFromOffice,
                      officeForTelehealth: newEventOfficeForTelehealth,
                      roomId: newEventRoom,
                      deviceIds: newEventDevices,
                    }}
                    onChange={(patch) => {
                      if (patch.date !== undefined) setNewEventDate(patch.date);
                      if (patch.startTime !== undefined) setNewEventStartTime(patch.startTime);
                      if (patch.endTime !== undefined) setNewEventEndTime(patch.endTime);
                      if (patch.recurringEnabled !== undefined) {
                        setShowRecurringOptions(patch.recurringEnabled);
                      }
                      if (patch.recurringInterval !== undefined) {
                        setRecurringInterval(patch.recurringInterval);
                      }
                      if (patch.recurringTimeframe !== undefined) {
                        setRecurringTimeframe(patch.recurringTimeframe);
                      }
                      if (patch.recurringDaysOfWeek !== undefined) {
                        setRecurringDaysOfWeek(patch.recurringDaysOfWeek);
                      }
                      if (patch.recurringEndType !== undefined) {
                        setRecurringEndType(patch.recurringEndType);
                      }
                      if (patch.recurringEndDate !== undefined) {
                        setRecurringEndDate(patch.recurringEndDate);
                      }
                      if (patch.recurringOccurrences !== undefined) {
                        setRecurringOccurrences(patch.recurringOccurrences);
                      }
                      if (patch.modality !== undefined) setNewEventModality(patch.modality);
                      if (patch.locationId !== undefined) setNewEventLocation(patch.locationId);
                      if (patch.telehealthFromOffice !== undefined) {
                        setNewEventTelehealthFromOffice(patch.telehealthFromOffice);
                      }
                      if (patch.officeForTelehealth !== undefined) {
                        setNewEventOfficeForTelehealth(patch.officeForTelehealth);
                      }
                      if (patch.roomId !== undefined) setNewEventRoom(patch.roomId);
                      if (patch.deviceIds !== undefined) setNewEventDevices(patch.deviceIds);
                    }}
                    sites={sites}
                    siteOptions={siteOptions}
                    firstSiteId={firstSiteId}
                    schedulingRooms={schedulingRooms}
                    portableDevices={schedulingPortableDevices}
                    fixedInRoom={(equipmentBySiteId[schedulingSiteId] ?? []).filter(
                      (e) =>
                        e.portability === 'fixed'
                        && e.homeRoomId === newEventRoom
                        && e.status === 'active',
                    )}
                    busyByRoom={busyByRoom}
                    busyByDevice={busyByDevice}
                    hideRecurringCheckbox={!!selectedEvent}
                    showRecurringEditAlert={
                      !!selectedEvent?.recurringPattern
                      && selectedEvent.recurringPattern !== 'none'
                    }
                    forceShowRecurrencePanel={
                      !!selectedEvent?.recurringPattern
                      && selectedEvent.recurringPattern !== 'none'
                    }
                    onViewRoomAvailability={() => setRoomAvailDrawerOpen(true)}
                    displayTimeZone={displayTimeZone}
                  />
                </>
              )}

              {newEventAppointmentType === 'meeting' && (
                <>
                  {manageablePractitionerSelectData.length > 1 && (
                    <Select
                      label="Calendar owner"
                      placeholder="Select practitioner"
                      value={newEventProvider || null}
                      onChange={(v) => setNewEventProvider(v ?? '')}
                      withAsterisk
                      leftSection={<IconUser size={16} />}
                      data={manageablePractitionerSelectData}
                      searchable
                    />
                  )}
                  <Select
                    label="Meeting type"
                    placeholder="Select meeting type"
                    value={newEventMeetingType || null}
                    onChange={(v) => setNewEventMeetingType(v ?? '')}
                    withAsterisk
                    data={meetingTypeSelectData}
                  />
                  <Select
                    label="Host"
                    placeholder="Select meeting host"
                    value={newEventMeetingHost || null}
                    onChange={(v) => setNewEventMeetingHost(v ?? '')}
                    withAsterisk
                    leftSection={<IconUser size={16} />}
                    data={providerSelectData}
                  />
                  <MultiSelect
                    label="Participants"
                    placeholder="Select meeting participants"
                    value={newEventMeetingParticipants}
                    onChange={setNewEventMeetingParticipants}
                    leftSection={<IconUsers size={16} />}
                    data={allProviderSelectData}
                    clearable
                  />
                  <Textarea
                    label="Meeting description"
                    placeholder="Enter meeting details"
                    value={newEventMeetingDescription}
                    onChange={(e) => setNewEventMeetingDescription(e.currentTarget.value)}
                    minRows={3}
                  />
                </>
              )}

              {newEventAppointmentType === 'time-entry' && (
                <>
                  {manageablePractitionerSelectData.length > 1 && (
                    <Select
                      label="Calendar owner"
                      placeholder="Select practitioner"
                      value={newEventProvider || null}
                      onChange={(v) => setNewEventProvider(v ?? '')}
                      withAsterisk
                      leftSection={<IconUser size={16} />}
                      data={manageablePractitionerSelectData}
                      searchable
                    />
                  )}
                  <Select
                    label="Time entry type"
                    placeholder="Select time entry type"
                    value={newEventTimeEntryType || null}
                    onChange={(v) => setNewEventTimeEntryType(v ?? '')}
                    withAsterisk
                    leftSection={<IconBan size={16} />}
                    data={timeEntryTypeSelectData}
                  />
                  <Textarea
                    label="Notes"
                    placeholder="Describe this time entry"
                    value={newEventTimeEntryNotes}
                    onChange={(e) => setNewEventTimeEntryNotes(e.currentTarget.value)}
                    withAsterisk
                    required
                    minRows={2}
                    error={appointmentForm.errors.timeEntryNotes}
                  />
                </>
              )}

              {/* Group uses shared GroupAppointmentScheduleFields above; other categories keep this block. */}
              {newEventAppointmentType !== 'group' && (
                <AppointmentDateTimeFields
                  values={appointmentForm.values}
                  onChange={applySessionFieldPatch}
                  errors={appointmentForm.errors}
                />
              )}

              {newEventAppointmentType !== 'group' && (
                <AppointmentRecurrenceFields
                  values={appointmentForm.values}
                  onChange={applySessionFieldPatch}
                  hideToggle={Boolean(selectedEvent)}
                  recurringSeries={Boolean(
                    selectedEvent?.recurringPattern && selectedEvent.recurringPattern !== 'none',
                  )}
                />
              )}

              {(newEventAppointmentType === 'session' || newEventAppointmentType === 'meeting') && (
                <AppointmentLocationFields
                  values={appointmentForm.values}
                  onChange={applySessionFieldPatch}
                  errors={appointmentForm.errors}
                  sites={sites}
                  siteOptions={siteOptions}
                  firstSiteId={firstSiteId}
                  rooms={schedulingRooms}
                  portableDevices={schedulingPortableDevices}
                  fixedInRoom={fixedInSelectedRoom}
                  busyByRoom={busyByRoom}
                  busyByDevice={busyByDevice}
                  bookingStart={roomHighlightRange.start}
                  bookingEnd={roomHighlightRange.end}
                  onViewRoomAvailability={() => setRoomAvailDrawerOpen(true)}
                />
              )}

              {newEventAppointmentType === 'session'
                && (newEventModality === 'telehealth'
                  || newEventModality === 'hybrid'
                  || newEventModality === 'in-person')
                && sessionRecordingConsented !== null && (
                <SessionRecordingConsentBadge consented={sessionRecordingConsented} mt="sm" />
              )}
          </>
        </AppointmentEditorModal>

        <Modal
          opened={recurringActionModalOpened}
          onClose={() => { setRecurringActionModalOpened(false); setPendingDragMove(null); }}
          title={recurringActionType === 'edit' ? 'Edit Recurring Event' : recurringActionType === 'move' ? 'Move Recurring Event' : 'Delete Recurring Event'}
          size="sm"
          centered
          styles={{
            header: { borderBottom: `1px solid ${BORDER_COLOR}`, paddingBottom: 12 },
            content: { border: `1px solid ${BORDER_COLOR}` },
            body: { padding: '16px 20px 20px' },
          }}
        >
          <Stack gap="lg">
            <Text size="sm" c="dimmed">
              {recurringActionType === 'edit'
                ? 'This is a recurring event. Do you want to update only this instance or all future occurrences?'
                : recurringActionType === 'move'
                  ? 'This is a recurring event. Do you want to move only this instance or all future occurrences?'
                  : 'This is a recurring event. Do you want to delete only this instance or all future occurrences?'}
            </Text>
            <Stack gap="xs">
              <Button
                variant="light"
                color={recurringActionType === 'delete' ? 'red' : 'blue'}
                onClick={() => (recurringActionType === 'edit' ? performSaveEvent('single') : recurringActionType === 'move' ? performDragMove('single') : performDeleteEvent('single'))}
                fullWidth
              >
                {recurringActionType === 'edit' ? 'Edit Only This Instance' : recurringActionType === 'move' ? 'Move Only This Instance' : 'Delete Only This Instance'}
              </Button>
              <Button
                variant="filled"
                color={recurringActionType === 'delete' ? 'red' : 'blue'}
                onClick={() => (recurringActionType === 'edit' ? performSaveEvent('all') : recurringActionType === 'move' ? performDragMove('all') : performDeleteEvent('all'))}
                fullWidth
              >
                {recurringActionType === 'edit' ? 'Edit All Future Instances' : recurringActionType === 'move' ? 'Move All Future Instances' : 'Delete All Future Instances'}
              </Button>
            </Stack>
            <Group justify="flex-end">
              <Button variant="outline" size="xs" onClick={() => { setRecurringActionModalOpened(false); setPendingDragMove(null); }}>
                Cancel
              </Button>
            </Group>
          </Stack>
        </Modal>

        <RoomAvailabilityDrawer
          opened={roomAvailDrawerOpen}
          onClose={() => setRoomAvailDrawerOpen(false)}
          locationId={roomDrawerLocationId}
          initialDate={newEventDate ?? new Date()}
          highlightStart={roomHighlightRange.start}
          highlightEnd={roomHighlightRange.end}
          selectedRoomId={newEventRoom}
          selectedDeviceIds={newEventDevices}
          officeSelectDisabled={newEventModality === 'telehealth' && !newEventTelehealthFromOffice}
          onOfficeLocationChange={(id: string) => {
            if (newEventModality === 'telehealth' && newEventTelehealthFromOffice) {
              setNewEventOfficeForTelehealth(id);
            } else if (newEventModality === 'in-person' || newEventModality === 'hybrid') {
              setNewEventLocation(id);
            }
          }}
          onPickRoom={(id) => setNewEventRoom(id)}
        />

        <RoomBrowseDrawer
          opened={roomBrowseDrawerOpen}
          onClose={() => setRoomBrowseDrawerOpen(false)}
        />
      </Container>
    </DndProvider>
  );
}
